/*!
 * Map Plaque Builder — Corazon Gift Co
 * Live map-plaque customizer.
 * Stack: fabric.js 4.6 (fixed 1000x1400 design canvas) + MapLibre GL (or Mapbox GL) rendered
 * offscreen and drawn into the canvas. Vanilla JS, no build step.
 */
(function () {
  'use strict';

  var CFG = window.MPB_CONFIG || {};
  var CANVAS_W = 1000, CANVAS_H = 1400;
  var MAP_BASE_CSS_W = 640;      // offscreen map CSS width
  var ZOOM_OFFSET = 1;           // map renders at slider zoom + 1
  var PIN_H = 92;                // pin height in canvas units for a 700-wide map window
  var PREVIEW_DEBOUNCE = 250;

  // ---------------------------------------------------------------- data
  var SHAPES = [
    { id: 'square', title: 'Square', map: { x: 150, y: 125, w: 700, h: 700 },
      text: { date: 925, title: 975, location: 1075, coords: 1250 } },
    // Rectangle = full-bleed map + white text band (matches the mockup previews).
    { id: 'rectangle', title: 'Rectangle', map: { x: 0, y: 0, w: 1000, h: 1080 }, whiteBg: true,
      text: { title: 1112, locationDate: 1222, coords: 1292 } },
    { id: 'heart', title: 'Heart', map: { x: 100, y: 85, w: 800, h: 800 },
      text: { date: 925, title: 975, location: 1075, coords: 1250 } },
    { id: 'circle', title: 'Circle', map: { x: 125, y: 100, w: 750, h: 750 },
      text: { date: 925, title: 975, location: 1075, coords: 1250 } },
    { id: 'house', title: 'House', map: { x: 100, y: 85, w: 800, h: 800 },
      text: { date: 925, title: 975, location: 1075, coords: 1250 } }
  ];

  var TEXT_SPEC = {
    title: { size: 85, weight: 'bold', fit: true, maxW: 925, min: 48, centerY: true },
    location: { size: 33, weight: 'normal', fit: true, maxW: 925, min: 18, centerY: true },
    locationDate: { size: 33, weight: 'normal', fit: true, maxW: 925, min: 18, centerY: true },
    date: { size: 33, weight: 'normal', fit: false, centerY: false },
    coords: { size: 33, weight: 'normal', fit: false, centerY: false }
  };

  var TITLE_EXAMPLES = ['Our First Kiss', 'Where It Began ❤️', 'Where It Began', 'Our First Date', 'Where We Met',
    'Where I Found You', 'The Beginning', 'First Date', 'Our Place', 'I love you 🩶', 'I Found You', 'My World',
    "It's Your Love", 'I Choose You 💍', 'Always & Forever', 'Nobody But You', 'Forever & Ever'];

  var DATE_FORMATS = ['dd-MM-yyyy', 'MM-dd-yyyy', 'MMMM do, yyyy', 'd MMMM yyyy', 'dd/MM/yyyy', 'MM/dd/yyyy',
    'dd.MM.yyyy', 'MM.dd.yyyy', 'MMMM yyyy', 'yyyy'];
  var EXAMPLE_DATE = new Date(2019, 9, 20);

  // Fonts offered for the map design.
  var FONTS = ['Arial', 'Abril Fatface', 'Alfa Slab One', 'Arvo', 'Fjalla One', 'Fredoka', 'Indie Flower', 'Lobster',
    'Nothing You Could Do', 'Pacifico', 'Permanent Marker', 'Playfair Display', 'Poppins', 'Roboto', 'Rowdies', 'Sail',
    'Satisfy', 'Spectral'];
  var GOOGLE_FONTS_CSS = 'https://fonts.googleapis.com/css2?family=Abril+Fatface&family=Alfa+Slab+One&family=Arvo:wght@400;700' +
    '&family=Fjalla+One&family=Fredoka:wght@400;700&family=Indie+Flower&family=Lobster&family=Nothing+You+Could+Do' +
    '&family=Pacifico&family=Permanent+Marker&family=Playfair+Display:wght@400;700&family=Poppins:wght@400;700' +
    '&family=Roboto:wght@400;700&family=Rowdies:wght@400;700&family=Sail&family=Satisfy&family=Spectral:wght@400;700&display=swap';

  var MARKER_COLORS = [
    { id: 'red', title: 'Red', color: '#d8503f', stroke: '#a4291f' },
    { id: 'navy', title: 'Navy', color: '#3c3f97', stroke: '#1c2797' },
    { id: 'gold', title: 'Gold', color: '#ddb553', stroke: '#96721b' },
    { id: 'black', title: 'Black', color: '#2a2a2a', stroke: '#000000' },
    { id: 'rose', title: 'Corazon red', color: '#BE1E23', stroke: '#6C0E0E' }
  ];
  var MARKERS = ['pin', 'heart-pin', 'heart', 'drop', 'house', 'pushpin', 'none'];
  var MARKER_TITLES = { pin: 'Pin', 'heart-pin': 'Heart pin', heart: 'Heart', drop: 'Drop', house: 'House', pushpin: 'Push pin', none: 'No marker' };

  // 8 color styles ("midnight" = the dark style).
  var MAP_STYLES = [
    { id: 'standard', title: 'Standard', p: { land: '#f2efe9', residential: '#efe6dc', green: '#c3e5a3', sand: '#f4ead0', water: '#9bdaf9', building: '#e4dcd2',
      road: '#ffffff', major: '#ffffff', motorway: '#f6d35b', roadCasing: '#e2d9cf', majorCasing: '#dcd2c6', motorwayCasing: '#e0b443', rail: '#d3cdc5', path: '#fbf8f4',
      labels: true, roadLabels: true, label: '#57534e', waterLabel: '#3f86b5', halo: '#ffffff' } },
    { id: 'shift-worker', title: 'Shift Worker', p: { land: '#ececec', residential: '#e9e9e9', green: '#e2e4e2', sand: '#ececec', water: '#a8c8dc', building: '#e2e2e2',
      road: '#ffffff', major: '#ffffff', motorway: '#8a8a8a', roadCasing: null, majorCasing: '#dddddd', motorwayCasing: null, rail: '#d5d5d5', path: '#f6f6f6',
      labels: true, roadLabels: false, label: '#7a7a7a', waterLabel: '#6d8fa5', halo: '#f2f2f2' } },
    { id: 'light-map', title: 'Light', p: { land: '#fcfcfc', residential: '#f7f7f7', green: '#efefef', sand: '#f6f6f6', water: '#c8c8c8', building: '#efefef',
      road: '#ffffff', major: '#ffffff', motorway: '#ffffff', roadCasing: '#e8e8e8', majorCasing: '#e2e2e2', motorwayCasing: '#d8d8d8', rail: '#e6e6e6', path: '#fafafa',
      labels: true, roadLabels: false, label: '#9a9a9a', waterLabel: '#9a9a9a', halo: '#ffffff' } },
    { id: 'midnight', title: 'Midnight', p: { land: '#1c2230', residential: '#212838', green: '#1d2e29', sand: '#262c38', water: '#0d1420', building: '#2a3142',
      road: '#363f54', major: '#47516b', motorway: '#c9a24d', roadCasing: null, majorCasing: null, motorwayCasing: null, rail: '#2f3647', path: '#2a3142',
      labels: true, roadLabels: false, label: '#a9b2c4', waterLabel: '#5d7290', halo: '#1c2230' } },
    { id: 'standard-blue-no-text', title: 'Standard Blue No Text', p: { land: '#f1f1ef', residential: '#ecebe8', green: '#bfe3a0', sand: '#f2ead2', water: '#6f96f6', building: '#e4e2de',
      road: '#ffffff', major: '#ffffff', motorway: '#f2c94c', roadCasing: '#deddda', majorCasing: '#dad8d4', motorwayCasing: '#e0a63b', rail: '#d5d4d0', path: '#fafafa',
      trunk: '#f0a640', labels: false, roadLabels: false } },
    { id: 'faded-blue', title: 'Faded Blue', p: { land: '#f8f5ee', residential: '#f3eee2', green: '#e2e8cf', sand: '#f4ecd7', water: '#c0cad6', building: '#efe8da',
      road: '#efe5cd', major: '#e9dbb7', motorway: '#e5cf9c', roadCasing: null, majorCasing: null, motorwayCasing: null, rail: '#e6dcc6', path: '#f3ecdc',
      labels: true, roadLabels: false, label: '#97917f', waterLabel: '#8a99ab', halo: '#f8f5ee' } },
    { id: 'red-hue', title: 'Red Hue', p: { land: '#ffffff', residential: '#fcecec', green: '#f7d6d6', sand: '#fbe6e6', water: '#ee8a8a', building: '#f7dede',
      road: '#ffffff', major: '#ffffff', motorway: '#ffffff', roadCasing: '#f5d6d6', majorCasing: '#f1caca', motorwayCasing: '#ecbcbc', rail: '#f3d2d2', path: '#fff6f6',
      labels: true, roadLabels: false, label: '#c46a6a', waterLabel: '#ffffff', halo: '#ffffff' } },
    { id: 'light-green', title: 'Light Blue', p: { land: '#ffffff', residential: '#fafafa', green: '#b9ddb2', sand: '#f5f1e4', water: '#8ccbd3', building: '#f0f0f0',
      road: '#ffffff', major: '#ffffff', motorway: '#ffffff', roadCasing: '#e3e3e3', majorCasing: '#d9d9d9', motorwayCasing: '#cccccc', rail: '#e4e4e4', path: '#fbfbfb',
      labels: true, roadLabels: false, label: '#7f8c8e', waterLabel: '#4f8f97', halo: '#ffffff' } }
  ];

  // ---------------------------------------------------------------- map provider switch
  // One config switch: CFG.map.provider = 'openfreemap' (default, free) | 'mapbox' (token + styles).
  var PROVIDERS = {
    openfreemap: {
      js: 'https://cdn.jsdelivr.net/npm/maplibre-gl@4.7.1/dist/maplibre-gl.js',
      css: 'https://cdn.jsdelivr.net/npm/maplibre-gl@4.7.1/dist/maplibre-gl.css',
      lib: function () { return window.maplibregl; },
      attribution: 'Map data © OpenFreeMap © OpenMapTiles © OpenStreetMap contributors',
      baseStyleUrl: 'https://tiles.openfreemap.org/styles/liberty',
      style: function (styleId) {
        return loadBaseStyle(this.baseStyleUrl).then(function (base) {
          var def = MAP_STYLES.filter(function (s) { return s.id === styleId; })[0] || MAP_STYLES[0];
          return applyPalette(base, def.p);
        });
      }
    },
    mapbox: {
      js: 'https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.js',
      css: 'https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.css',
      lib: function () { var m = window.mapboxgl; if (m && CFG.map && CFG.map.mapboxToken) m.accessToken = CFG.map.mapboxToken; return m; },
      attribution: 'Map data © Mapbox © OpenStreetMap contributors',
      style: function (styleId) {
        var styles = parseJSON(CFG.map && CFG.map.mapboxStyles) || {};
        return Promise.resolve(styles[styleId] || 'mapbox://styles/mapbox/streets-v12');
      }
    }
  };
  var PROVIDER = PROVIDERS[(CFG.map && CFG.map.provider) || 'openfreemap'] || PROVIDERS.openfreemap;

  // ---------------------------------------------------------------- state
  var DEFAULT_LOC = CFG.defaultLocation || { lat: 34.052235, lng: -118.243683, label: 'Los Angeles, CA, USA' };
  var state = {
    sizeIndex: 0,
    shape: 'square',
    stand: (CFG.stands && CFG.stands[0] && CFG.stands[0].key) || 'none',
    lat: DEFAULT_LOC.lat, lng: DEFAULT_LOC.lng,
    searchLabel: '',
    usingDefaultLocation: true,
    locationManuallyEdited: false,
    zoom: Number(CFG.defaultZoom) || 13,
    mapStyle: 'standard',
    markerColor: 4,
    marker: 0,
    title: 'Where It Began ❤️',
    locationText: DEFAULT_LOC.label,
    date: null,               // ISO yyyy-mm-dd
    dateFormat: 'MM-dd-yyyy',
    showDate: true,
    showCoords: true,
    font: 'Arial'
  };

  var ui = {}, manifest = null, assetBase = '', fabricCanvas = null, mapObj = null, bgRect = null, textObjs = {};
  var renderMap = null, renderMapStyleId = null, renderMapShape = null;
  var renderBusy = false, renderDirty = false, previewTimer = null, lastMapCanvas = null;
  var maskCache = {}, pinCache = {};
  var errorFields = [], validationActive = false;
  var STEPS = 5, STEP_NAMES = ['', 'Place', 'Style', 'Words', 'Size', 'Stand'];
  var NEXT_LABELS = ['', 'Next: map style', 'Next: your words', 'Next: shape and size', 'Next: add a stand', 'Add to cart'];
  var step = 1, maxStep = 1;

  // ---------------------------------------------------------------- utils
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function parseJSON(v) { if (v && typeof v === 'object') return v; try { return v ? JSON.parse(v) : null; } catch (e) { return null; } }
  function clone(o) { return JSON.parse(JSON.stringify(o)); }
  function escapeHtml(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]; }); }
  function debounce(fn, ms) { var t; return function () { var a = arguments, s = this; clearTimeout(t); t = setTimeout(function () { fn.apply(s, a); }, ms); }; }
  function makeId(n) { var c = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789', r = ''; for (var i = 0; i < n; i++) r += c.charAt(Math.floor(Math.random() * c.length)); return r; }
  function currentShape() { return SHAPES.filter(function (s) { return s.id === state.shape; })[0] || SHAPES[0]; }
  function currentSize() { return (CFG.sizes || [])[state.sizeIndex] || {}; }
  function currentStand() { return (CFG.stands || []).filter(function (s) { return s.key === state.stand; })[0] || { key: 'none', label: 'Without Stand', price: 0 }; }
  function currentStyle() { return MAP_STYLES.filter(function (s) { return s.id === state.mapStyle; })[0] || MAP_STYLES[0]; }

  function loadScript(src) {
    return new Promise(function (res, rej) {
      if (document.querySelector('script[data-mpb-src="' + src + '"]')) { return waitFor(function () { return true; }).then(res); }
      var s = document.createElement('script'); s.src = src; s.async = true; s.setAttribute('data-mpb-src', src);
      s.onload = function () { res(); }; s.onerror = function () { rej(new Error('Failed to load ' + src)); };
      document.head.appendChild(s);
    });
  }
  function loadCss(href) {
    if (document.querySelector('link[href="' + href + '"]')) return;
    var l = document.createElement('link'); l.rel = 'stylesheet'; l.href = href; document.head.appendChild(l);
  }
  function waitFor(test, timeout) {
    return new Promise(function (res, rej) {
      var t0 = Date.now();
      (function tick() { if (test()) return res(); if (Date.now() - t0 > (timeout || 20000)) return rej(new Error('timeout')); setTimeout(tick, 30); })();
    });
  }
  function loadImage(src) {
    return new Promise(function (res, rej) {
      var img = new Image(); img.crossOrigin = 'anonymous';
      img.onload = function () { res(img); }; img.onerror = function () { rej(new Error('img ' + src)); };
      img.src = src;
    });
  }
  function assetUrl(file) {
    if (!file) return '';
    if (/^(https?:)?\/\//.test(file) || file.indexOf('data:') === 0) return file;
    return assetBase + file + (manifest && manifest.version ? '?v=' + encodeURIComponent(manifest.version) : '');
  }
  /* ---------- money (Transcy-safe; ported from cgc-gift-builder.js / tt-rose.js) ----------
     Cart + checkout are USD. The store's currency app (Transcy) only converts the DISPLAY (C$ in Canada), and when it
     rewrites our price nodes it drops their markup (the struck-through was-price turned into a second bold price).
     So the builder converts itself with Transcy's own selection and rate, PER LINE in whole units like Transcy (lines
     add up to the shown total), and emits Transcy's "already converted" <tc-money class="money-done--XXX"> markup so
     Transcy leaves those nodes alone. Was-prices are a <s class="mpb-was"> wrapper around that node. */
  var CUR = CFG.currency || (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
  var DISP = { code: CUR, rate: 1 };
  function fmtFor(code) { try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: code }); } catch (e) { return null; } }
  var fmt = fmtFor(CUR);
  function syncDisplay() {
    var code = CUR, rate = 1;
    try {
      var t = window._transcy, v = t && t.variants, info = v && v.currencyActiveInfo;
      var want = v && ((v.currencyTargetInfo && v.currencyTargetInfo.code) || v.currencyCurrent);
      if (want && want !== CUR && info && info.code === CUR && info.detail && info.detail.convert && info.detail.convert[want]) { code = want; rate = +info.detail.convert[want]; }
    } catch (e) {}
    var changed = code !== DISP.code || rate !== DISP.rate;
    DISP = { code: code, rate: rate }; fmt = fmtFor(code);
    return changed;
  }
  function D(cents) { cents = Number(cents) || 0; return DISP.code === CUR ? cents : Math.round(cents / 100 * DISP.rate) * 100; }
  function fmtDisp(dcents) {
    var n = dcents / 100, out = fmt ? fmt.format(n) : '$' + n.toFixed(2);
    if (DISP.code === 'CAD') out = out.replace('CA$', 'C$');
    if (DISP.code !== CUR) out = out.replace(/\.00$/, '');
    /* zero-width space after the symbol: Transcy's /\$\s?\d+/ can't match it and convert it a second time */
    return DISP.code === CUR ? out : out.replace(/([^\d\s])(\d)/, '$1\u200b$2');
  }
  function mD(dcents) {
    return DISP.code === CUR ? '<span class="mpb-money">' + fmtDisp(dcents) + '</span>'
      : '<tc-money translate="no" class="mpb-money money-done--' + DISP.code + '">' + fmtDisp(dcents) + '</tc-money>';
  }
  function money(cents) { return mD(D(cents)); }            // shop-currency cents -> display HTML
  function was(cents) { return ' <s class="mpb-was">' + money(cents) + '</s>'; }

  // date formatting for the subset of date-fns tokens the formats use
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  function ordinal(n) { var s = ['th', 'st', 'nd', 'rd'], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); }
  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function formatDate(d, fmt) {
    return fmt.replace(/MMMM|yyyy|do|dd|MM|d/g, function (t) {
      switch (t) {
        case 'MMMM': return MONTHS[d.getMonth()];
        case 'yyyy': return String(d.getFullYear());
        case 'do': return ordinal(d.getDate());
        case 'dd': return pad(d.getDate());
        case 'MM': return pad(d.getMonth() + 1);
        case 'd': return String(d.getDate());
      }
      return t;
    });
  }
  function parseISODate(iso) { if (!iso) return null; var p = iso.split('-'); var d = new Date(+p[0], +p[1] - 1, +p[2]); return isNaN(d) ? null : d; }
  function limitDp(n, dp) { var f = Number(n).toFixed(dp); return f.indexOf('.') === -1 ? f : f.replace(/\.?0+$/, ''); }
  function coordsText() { return limitDp(state.lat, 5) + ', ' + limitDp(state.lng, 5); }
  function dateText() { var d = parseISODate(state.date) || EXAMPLE_DATE; return formatDate(d, state.dateFormat); }

  // ---------------------------------------------------------------- map style palette
  var baseStyleCache = {};
  function loadBaseStyle(url) {
    if (!baseStyleCache[url]) baseStyleCache[url] = fetch(url).then(function (r) { if (!r.ok) throw new Error('style ' + r.status); return r.json(); });
    return baseStyleCache[url].then(clone);
  }
  function classify(id, type) {
    if (type === 'background') return 'land';
    if (/natural_earth|landcover_wetland|road_area_pattern|building-3d|^poi_|shield|one_way|^airport|aeroway|boundary|landcover_ice|park_outline/.test(id)) return 'hide';
    if (id === 'water') return 'water';
    if (/^waterway/.test(id) && type === 'line') return 'water';
    if (type === 'fill' && /park|landcover_grass|landcover_wood|landuse_pitch|landuse_cemetery|landuse_track/.test(id)) return 'green';
    if (type === 'fill' && /landuse_residential|landuse_hospital|landuse_school/.test(id)) return 'residential';
    if (type === 'fill' && /landcover_sand/.test(id)) return 'sand';
    if (id === 'building') return 'building';
    if (type === 'line' && /rail/.test(id)) return 'rail';
    if (type === 'line' && /path_pedestrian/.test(id)) return 'path';
    if (type === 'line' && /casing/.test(id)) return /motorway/.test(id) ? 'motorwayCasing' : (/trunk_primary|secondary_tertiary/.test(id) ? 'majorCasing' : 'roadCasing');
    if (type === 'line' && /motorway/.test(id)) return 'motorway';
    if (type === 'line' && /trunk_primary/.test(id)) return 'trunk';
    if (type === 'line' && /secondary_tertiary/.test(id)) return 'major';
    if (type === 'line' && /^(road|bridge|tunnel)_/.test(id)) return 'road';
    if (type === 'symbol' && /highway-name/.test(id)) return 'roadLabel';
    if (type === 'symbol' && /water_name|waterway_line_label/.test(id)) return 'waterLabel';
    if (type === 'symbol' && /^label_/.test(id)) return 'placeLabel';
    return 'keep';
  }
  function applyPalette(style, p) {
    style.layers = style.layers.filter(function (l) {
      var cat = classify(l.id, l.type);
      l.layout = l.layout || {}; l.paint = l.paint || {};
      if (cat === 'hide') return false;
      if (cat === 'land') { l.paint['background-color'] = p.land; return true; }
      if (l.type === 'fill') {
        var fc = p[cat]; if (!fc) return cat === 'keep';
        delete l.paint['fill-pattern']; l.paint['fill-color'] = fc; l.paint['fill-outline-color'] = fc; l.paint['fill-opacity'] = 1;
        return true;
      }
      if (l.type === 'line') {
        var key = cat === 'trunk' ? 'major' : cat;
        var lc = p[key];
        if (lc === null || lc === undefined) return cat === 'keep';
        if (cat === 'trunk' && p.trunk) lc = ['match', ['get', 'class'], 'trunk', p.trunk, p.major];
        delete l.paint['line-dasharray']; l.paint['line-color'] = lc; l.paint['line-opacity'] = 1;
        return true;
      }
      if (l.type === 'symbol') {
        if (!p.labels || cat === 'keep') return false;
        if (cat === 'roadLabel' && !p.roadLabels) return false;
        l.paint['text-color'] = cat === 'waterLabel' ? (p.waterLabel || p.label) : p.label;
        l.paint['text-halo-color'] = p.halo; l.paint['text-halo-width'] = 1.4; l.paint['text-halo-blur'] = 0.5;
        delete l.layout['icon-image'];
        return true;
      }
      return cat !== 'keep' || l.type === 'raster' ? cat !== 'keep' : true;
    });
    return style;
  }

  // ---------------------------------------------------------------- pins (SVG, drawn crisp at any resolution)
  function pinSvg(kind, c) {
    var col = c.color, st = c.stroke;
    var body;
    switch (kind) {
      case 'pin': body = '<path d="M50 4C26 4 8 22 8 45c0 31 42 72 42 72s42-41 42-72C92 22 74 4 50 4z" fill="' + col + '" stroke="' + st + '" stroke-width="4"/><circle cx="50" cy="44" r="15" fill="#fff"/>'; break;
      case 'heart-pin': body = '<path d="M50 4C26 4 8 22 8 45c0 31 42 72 42 72s42-41 42-72C92 22 74 4 50 4z" fill="' + col + '" stroke="' + st + '" stroke-width="4"/><path d="M50 62c-3-3-20-13-20-25 0-7 5-11 11-11 4 0 7 2 9 6 2-4 5-6 9-6 6 0 11 4 11 11 0 12-17 22-20 25z" fill="#fff"/>'; break;
      case 'heart': body = '<path d="M50 112C46 108 6 80 6 46 6 26 20 12 37 12c6 0 10 2 13 6 3-4 7-6 13-6 17 0 31 14 31 34 0 34-40 62-44 66z" fill="' + col + '" stroke="' + st + '" stroke-width="4"/>'; break;
      case 'drop': body = '<path d="M50 6C50 6 14 52 14 76a36 36 0 0 0 72 0C86 52 50 6 50 6z" fill="' + col + '" stroke="' + st + '" stroke-width="4"/>'; break;
      case 'house': body = '<path d="M50 4C26 4 8 22 8 45c0 31 42 72 42 72s42-41 42-72C92 22 74 4 50 4z" fill="' + col + '" stroke="' + st + '" stroke-width="4"/><path d="M50 24 28 43h7v18h11V50h8v11h11V43h7z" fill="#fff"/>'; break;
      case 'pushpin': body = '<rect x="47" y="56" width="6" height="58" rx="3" fill="#8b8b8b"/><circle cx="50" cy="36" r="30" fill="' + col + '" stroke="' + st + '" stroke-width="4"/><circle cx="40" cy="26" r="8" fill="#fff" opacity=".45"/>'; break;
      default: return null;
    }
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 120" width="100" height="120">' + body + '</svg>';
  }
  function pinImage(kind, colorIdx) {
    var key = kind + '|' + colorIdx;
    if (!pinCache[key]) {
      var svg = pinSvg(kind, MARKER_COLORS[colorIdx]);
      pinCache[key] = svg ? loadImage('data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg)) : Promise.resolve(null);
    }
    return pinCache[key];
  }
  function maskImage(shapeId) {
    var file = manifest && manifest.masks && manifest.masks[shapeId];
    if (!file || shapeId === 'square' || shapeId === 'rectangle') return Promise.resolve(null);
    if (!maskCache[shapeId]) maskCache[shapeId] = loadImage(assetUrl(file)).catch(function () { return null; });
    return maskCache[shapeId];
  }

  // ---------------------------------------------------------------- offscreen map rendering
  function mapCssSize(shape) { return { w: MAP_BASE_CSS_W, h: Math.round(MAP_BASE_CSS_W * shape.map.h / shape.map.w) }; }

  // Resolves once the map has drawn everything it needs. Don't rely on the 'idle' event alone: a style with no symbol
  // layers (Standard Blue No Text) finishes in the same frame as 'load', so 'idle' fires before a listener added after
  // 'load' can hear it and the old code sat out its 20-30 s timeouts on every preview and every print. Instead: after at
  // least one fresh frame, poll loaded()/areTilesLoaded(), then take one more frame so the canvas holds the final draw.
  function waitIdle(map, maxMs) {
    return new Promise(function (res) {
      var done = false, framed = false, settling = false, iv = 0, cap = 0;
      function fin() {
        if (done) return; done = true; clearInterval(iv); clearTimeout(cap);
        try { map.off('idle', fin); map.off('render', onRender); } catch (e) { /* ignore */ }
        res();
      }
      function ready() { try { return map.loaded() && (!map.areTilesLoaded || map.areTilesLoaded()) && !map.isMoving(); } catch (e) { return false; } }
      function onRender() { framed = true; if (settling) fin(); }
      map.on('idle', fin);
      map.on('render', onRender);
      iv = setInterval(function () {
        if (framed && !settling && ready()) { settling = true; try { map.triggerRepaint(); } catch (e) { fin(); } setTimeout(fin, 300); }
      }, 60);
      cap = setTimeout(fin, maxMs || 30000);
      try { map.triggerRepaint(); } catch (e) { /* ignore */ }
    });
  }
  // 'load' with a deadline and the map's own errors, so a stuck or lost WebGL map can't leave the shopper waiting forever
  function waitLoad(map, ms) {
    return new Promise(function (res, rej) {
      var t = setTimeout(function () { rej(new Error('map load timeout')); }, ms || 25000);
      map.once('load', function () { clearTimeout(t); res(); });
      try { map.getCanvas().addEventListener('webglcontextlost', function () { clearTimeout(t); rej(new Error('map context lost')); }); } catch (e) { /* ignore */ }
      if (map.loaded && map.loaded()) { clearTimeout(t); res(); }
    });
  }

  function makeMap(container, pixelRatio, style, interactive) {
    var GL = PROVIDER.lib();
    var opts = {
      container: container, style: style, center: [state.lng, state.lat], zoom: state.zoom + ZOOM_OFFSET,
      interactive: !!interactive, attributionControl: false, preserveDrawingBuffer: true, fadeDuration: 0,
      pixelRatio: pixelRatio, maxCanvasSize: [8192, 8192], antialias: true
    };
    if (!interactive) { opts.renderWorldCopies = false; }
    return new GL.Map(opts);
  }

  function ensureLib() {
    loadCss(PROVIDER.css);
    if (PROVIDER.lib()) return Promise.resolve();
    return loadScript(PROVIDER.js).then(function () { return waitFor(function () { return !!PROVIDER.lib(); }); });
  }

  // Renders map + pin + shape mask into a canvas of (window * scale) px.
  function renderMapCanvas(scale, opts) {
    opts = opts || {};
    var shape = currentShape();
    var css = mapCssSize(shape);
    var outW = Math.round(shape.map.w * scale), outH = Math.round(shape.map.h * scale);
    var pr = outW / css.w;
    return ensureLib().then(function () { return PROVIDER.style(state.mapStyle); }).then(function (style) {
      var holder = $('#mpb-render-map');
      if (opts.print) {
        var tmp = document.createElement('div');
        tmp.style.width = css.w + 'px'; tmp.style.height = css.h + 'px';
        holder.parentNode.appendChild(tmp);
        var m;
        var cleanup = function () { try { if (m) m.remove(); } catch (e) { /* ignore */ } if (tmp.parentNode) tmp.parentNode.removeChild(tmp); };
        try { m = makeMap(tmp, pr, style, false); } catch (e) { cleanup(); return Promise.reject(e); }
        return waitLoad(m, opts.loadMs || 25000).then(function () { return waitIdle(m, opts.idleMs || 25000); }).then(function () {
          var c = m.getCanvas();
          var out = { canvas: copyCanvas(c, outW, outH), glW: c.width, glH: c.height };
          cleanup();
          return out;
        }, function (err) { cleanup(); throw err; });
      }
      holder.style.width = css.w + 'px'; holder.style.height = css.h + 'px';
      var ready;
      if (!renderMap) {
        renderMap = makeMap(holder, pr, style, false);
        renderMapStyleId = state.mapStyle; renderMapShape = shape.id;
        ready = new Promise(function (res) { renderMap.once('load', res); });
      } else {
        if (renderMapShape !== shape.id) { renderMap.resize(); renderMapShape = shape.id; }
        if (typeof renderMap.setPixelRatio === 'function') renderMap.setPixelRatio(pr);
        if (renderMapStyleId !== state.mapStyle) { renderMap.setStyle(style, { diff: false }); renderMapStyleId = state.mapStyle; }
        renderMap.jumpTo({ center: [state.lng, state.lat], zoom: state.zoom + ZOOM_OFFSET });
        ready = Promise.resolve();
      }
      return ready.then(function () {
        renderMap.jumpTo({ center: [state.lng, state.lat], zoom: state.zoom + ZOOM_OFFSET });
        return waitIdle(renderMap);
      }).then(function () {
        var c = renderMap.getCanvas();
        return { canvas: copyCanvas(c, outW, outH), glW: c.width, glH: c.height };
      });
    }).then(function (res) {
      return Promise.all([pinImage(MARKERS[state.marker], state.markerColor), maskImage(shape.id)]).then(function (arr) {
        var ctx = res.canvas.getContext('2d');
        var pin = arr[0], mask = arr[1];
        if (pin) {
          var ph = PIN_H * scale * (shape.map.w / 700), pw = ph * (100 / 120);
          var cx = outW / 2, cy = outH / 2;
          var anchorBottom = MARKERS[state.marker] !== 'heart';
          ctx.drawImage(pin, cx - pw / 2, anchorBottom ? cy - ph : cy - ph / 2, pw, ph);
        }
        if (mask) {
          // mask is cropped to its shape: fit it (contain, centered) inside the map rect, clear outside
          var mw = mask.naturalWidth || mask.width || 1, mh = mask.naturalHeight || mask.height || 1;
          var k = Math.min(outW / mw, outH / mh), dw = mw * k, dh = mh * k;
          ctx.globalCompositeOperation = 'destination-in';
          ctx.drawImage(mask, (outW - dw) / 2, (outH - dh) / 2, dw, dh);
          ctx.globalCompositeOperation = 'source-over';
        }
        res.canvas.dataset.glw = res.glW; res.canvas.dataset.glh = res.glH;
        return res.canvas;
      });
    });
  }
  function copyCanvas(src, w, h) {
    var c = document.createElement('canvas'); c.width = w; c.height = h;
    c.getContext('2d').drawImage(src, 0, 0, w, h);
    return c;
  }

  // ---------------------------------------------------------------- fabric canvas
  function fontFamilyCss(f) { return f === 'Arial' ? 'Arial, Helvetica, sans-serif' : '"' + f + '"'; }
  function fitFontSize(text, spec) {
    if (!spec.fit) return spec.size;
    var probe = function (size) { return new fabric.Text(text || ' ', { fontFamily: fontFamilyCss(state.font), fontWeight: spec.weight, fontSize: size }).width; };
    var w = probe(spec.size);
    if (w <= spec.maxW) return spec.size;
    var f = Math.max(spec.min, Math.min(spec.size, Math.floor(spec.size * spec.maxW / w)));
    var fw = probe(f);
    if (fw > spec.maxW && f > spec.min) f = Math.max(spec.min, Math.floor(f * spec.maxW / fw));
    return f;
  }
  function textEntries() {
    var shape = currentShape(), t = shape.text, out = {};
    out.title = { text: state.title || '', y: t.title };
    if (t.locationDate != null) {
      var loc = state.locationText || DEFAULT_LOC.label;
      out.locationDate = { text: state.showDate ? loc + ' – ' + dateText() : loc, y: t.locationDate };
    } else {
      out.location = { text: state.locationText || '', y: t.location };
      out.date = { text: state.showDate ? dateText() : '', y: t.date };
    }
    out.coords = { text: state.showCoords ? coordsText() : '', y: t.coords };
    return out;
  }
  function layoutText() {
    if (!fabricCanvas) return;
    if (!bgRect) { bgRect = new fabric.Rect({ left: 0, top: 0, width: CANVAS_W, height: CANVAS_H, fill: '#ffffff', selectable: false }); fabricCanvas.insertAt(bgRect, 0); }
    bgRect.set({ visible: !!currentShape().whiteBg });
    var entries = textEntries();
    Object.keys(textObjs).forEach(function (k) { if (!entries[k]) { fabricCanvas.remove(textObjs[k]); delete textObjs[k]; } });
    Object.keys(entries).forEach(function (k) {
      var e = entries[k], spec = TEXT_SPEC[k];
      var size = fitFontSize(e.text, spec);
      var top = spec.centerY ? e.y + Math.round(spec.size / 2) : e.y;
      var props = { text: e.text, fontFamily: fontFamilyCss(state.font), fontWeight: spec.weight, fontSize: size, fill: '#000000',
        textAlign: 'center', originX: 'center', originY: spec.centerY ? 'center' : 'top', left: CANVAS_W / 2, top: top, visible: !!e.text };
      if (!textObjs[k]) { textObjs[k] = new fabric.Text(e.text, props); fabricCanvas.add(textObjs[k]); }
      else { textObjs[k].set(props); textObjs[k].initDimensions && textObjs[k].initDimensions(); textObjs[k].setCoords(); }
    });
    spaceTextLines(entries);
    fabricCanvas.requestRenderAll();
  }
  // The lines sit at fixed heights per shape. Script fonts (Indie Flower, Pacifico, Satisfy, Lobster...) hang their
  // g/y/p tails well below the box those heights assume, so a tail could run into the line under it (order CGC-66119).
  // Measure each line's real ink and push the lines below down until there is a clear gap; if that would run off the
  // plaque, shrink the title a step and try again.
  var LINE_GAP = 14, BOTTOM_MARGIN = 24;
  // Fabric 4.6 draws a one-line Text with its baseline on the bottom edge of the object's box (box height =
  // fontSize x 1.13), so tails hang outside the box and object snapshots clip them. Measure with the browser instead.
  var measureCtx = null;
  function inkSpan(obj) {
    if (!obj || !obj.visible || !obj.text) return null;
    measureCtx = measureCtx || document.createElement('canvas').getContext('2d');
    measureCtx.font = typeof obj._getFontDeclaration === 'function' ? obj._getFontDeclaration()
      : (obj.fontWeight + ' ' + obj.fontSize + 'px ' + obj.fontFamily);
    var m = measureCtx.measureText(obj.text), br = obj.getBoundingRect(true, true), base = br.top + br.height;
    if (m.actualBoundingBoxAscent == null) return { top: br.top, bottom: base + obj.fontSize * 0.3 };
    return { top: base - m.actualBoundingBoxAscent, bottom: base + m.actualBoundingBoxDescent };
  }
  function spaceTextLines(entries) {
    var order = Object.keys(entries).filter(function (k) { return textObjs[k] && textObjs[k].visible; })
      .sort(function (a, b) { return entries[a].y - entries[b].y; });
    if (order.length < 2) return;
    for (var attempt = 0; attempt < 6; attempt++) {
      var prev = null, lowest = 0;
      order.forEach(function (k) {
        var span = inkSpan(textObjs[k]);
        if (!span) return;
        if (prev && span.top - prev.bottom < LINE_GAP) {
          var dy = Math.ceil(LINE_GAP - (span.top - prev.bottom));
          textObjs[k].set({ top: textObjs[k].top + dy }); textObjs[k].setCoords();
          span = { top: span.top + dy, bottom: span.bottom + dy };
        }
        prev = span; lowest = span.bottom;
      });
      var t = textObjs.title;
      if (lowest <= CANVAS_H - BOTTOM_MARGIN || !t || t.fontSize <= TEXT_SPEC.title.min) return;
      // ran off the bottom: put the lines back, shrink the title a step, and space again
      order.forEach(function (k) {
        var e = entries[k], spec = TEXT_SPEC[k];
        textObjs[k].set({ top: spec.centerY ? e.y + Math.round(spec.size / 2) : e.y }); textObjs[k].setCoords();
      });
      t.set({ fontSize: Math.max(TEXT_SPEC.title.min, Math.round(t.fontSize * 0.9)) });
      t.initDimensions && t.initDimensions(); t.setCoords();
    }
  }
  function placeMapObject(canvasEl, scale) {
    var shape = currentShape();
    if (!mapObj) {
      mapObj = new fabric.Image(canvasEl, { left: shape.map.x, top: shape.map.y, scaleX: 1 / scale, scaleY: 1 / scale, objectCaching: false });
      fabricCanvas.insertAt(mapObj, bgRect ? 1 : 0);
    } else {
      mapObj.setElement(canvasEl);
      mapObj.set({ left: shape.map.x, top: shape.map.y, scaleX: 1 / scale, scaleY: 1 / scale, width: canvasEl.width, height: canvasEl.height });
    }
    mapObj.dirty = true;
    fabricCanvas.requestRenderAll();
  }

  function schedulePreview(immediate) {
    clearTimeout(previewTimer);
    showLoader(true);
    previewTimer = setTimeout(runPreview, immediate ? 0 : PREVIEW_DEBOUNCE);
  }
  function runPreview() {
    if (renderBusy) { renderDirty = true; return; }
    renderBusy = true; renderDirty = false;
    renderMapCanvas(1).then(function (c) {
      lastMapCanvas = c;
      placeMapObject(c, 1);
      layoutText();
    }).catch(function (err) { console.error('[mpb] map render failed', err); }).then(function () {
      renderBusy = false;
      if (renderDirty) { runPreview(); return; }
      showLoader(false);
      markReady();
    });
  }
  function showLoader(on) { if (ui.loader) ui.loader.hidden = !on; }
  var readyResolved = false, readyResolve;
  var readyPromise = new Promise(function (r) { readyResolve = r; });
  function markReady() { if (!readyResolved) { readyResolved = true; ui.app.removeAttribute('data-loading'); readyResolve(); } }

  function resizeCanvasCss() {
    if (!fabricCanvas || !ui.canvasBox) return;
    var w = ui.canvasBox.clientWidth;
    if (!w) return;
    fabricCanvas.setDimensions({ width: w + 'px', height: Math.round(w * CANVAS_H / CANVAS_W) + 'px' }, { cssOnly: true });
  }

  // ---------------------------------------------------------------- mockup layers
  function applyStandLayers() {
    if (!manifest) return;
    var entry = (manifest.stands || {})[state.stand] || (manifest.stands || {}).none;
    if (!entry) return;
    var stage = entry.stage || { w: CANVAS_W, h: CANVAS_H, plaque: { x: 0, y: 0, w: CANVAS_W, h: CANVAS_H } };
    var pl = stage.plaque || { x: 0, y: 0, w: CANVAS_W, h: CANVAS_H };
    ui.stage.style.aspectRatio = stage.w + ' / ' + stage.h;
    ui.stage.style.paddingTop = '';
    ui.canvasBox.style.left = (100 * pl.x / stage.w) + '%';
    ui.canvasBox.style.top = (100 * pl.y / stage.h) + '%';
    ui.canvasBox.style.width = (100 * pl.w / stage.w) + '%';
    ui.back.src = assetUrl(entry.back);
    ui.front.src = assetUrl(entry.front);
    ui.back.hidden = !entry.back; ui.front.hidden = !entry.front;
    requestAnimationFrame(resizeCanvasCss);
    panPreview();
  }

  // ---------------------------------------------------------------- UI building
  function shapeIconSvg(id) {
    var f = 'fill="#F6E0DA" stroke="#BE1E23" stroke-width="2.4" stroke-linejoin="round"';
    var shapes = {
      square: '<rect x="7" y="7" width="34" height="34" rx="2" ' + f + '/>',
      rectangle: '<rect x="10" y="4" width="28" height="40" rx="2" ' + f + '/>',
      heart: '<path d="M24 42C20 39 4 29.5 4 16.5 4 10 8.6 5.5 14.3 5.5c4.1 0 7.6 2.2 9.7 5.7 2.1-3.5 5.6-5.7 9.7-5.7C39.4 5.5 44 10 44 16.5 44 29.5 28 39 24 42z" ' + f + '/>',
      circle: '<circle cx="24" cy="24" r="19" ' + f + '/>',
      house: '<path d="M24 5 43 21.5h-4.5V43h-29V21.5H5z" ' + f + '/>'
    };
    return '<svg viewBox="0 0 48 48" aria-hidden="true">' + shapes[id] + '</svg>';
  }
  function chip(t) { return t ? '<span class="mpb-chip">' + escapeHtml(t) + '</span>' : ''; }

  function buildSizes() {
    var n = (CFG.sizes || []).length;
    ui.sizes.innerHTML = (CFG.sizes || []).map(function (s, i) {
      var w = Math.round(14 + 10 * (n > 1 ? i / (n - 1) : 0));
      var cmp = s.compareAt && s.compareAt > s.price ? was(s.compareAt) : '';
      return '<button type="button" class="mpb-tile mpb-size-btn" role="radio" data-size="' + i + '"' + (s.variantId ? '' : ' disabled') + '>' + chip(s.badge) +
        '<span class="mpb-sz-box"><span class="mpb-sz-bar" style="--w:' + w + 'px"></span></span><span class="mpb-sz-l">' + escapeHtml(s.label) + '</span>' +
        '<span class="mpb-sz-d">' + escapeHtml(s.short || '') + '</span><span class="mpb-sz-p">' + money(s.price) + cmp + '</span></button>';
    }).join('');
    var rows = (CFG.sizes || []).map(function (s) { return '<tr><th>' + escapeHtml(s.label) + '</th><td>' + escapeHtml(s.dims || s.short || '') + '</td><td>' + money(s.price) + '</td></tr>'; }).join('');
    $('#mpb-size-table').innerHTML = rows;
  }
  function rerenderPrices() { buildSizes(); buildStands(); fillFromPrice(); syncSelections(); }
  // title-block price row: the current SIZE only (base price, owner 10-23: like TransparentTracks and the collection
  // card); the stand adds in the tally at step 5 and on Add to cart. Struck compare-at only, no Save % tag.
  function fillFromPrice() {
    var tb = $('#mpb-tbprice'); if (!tb) return;
    var s = currentSize(); if (!s.price) return;
    function cmp(x) { return x.compareAt && x.compareAt > x.price ? x.compareAt : x.price; }
    var now = D(s.price), w = D(cmp(s));
    tb.innerHTML = '<span class="cgc-tb__now">' + mD(now) + '</span>' + (w > now ? '<s class="cgc-tb__was">' + mD(w) + '</s>' : '');
  }
  function buildShapes() {
    ui.shapes.innerHTML = SHAPES.map(function (s) {
      return '<button type="button" class="mpb-tile mpb-shape-btn" role="radio" data-shape="' + s.id + '" aria-label="' + s.title + '">' + shapeIconSvg(s.id) + '<span>' + s.title + '</span></button>';
    }).join('');
  }
  function buildStyles() {
    ui.styles.innerHTML = MAP_STYLES.map(function (s) {
      var name = s.id === 'standard-blue-no-text' ? 'Blue, no text' : s.title;
      return '<button type="button" class="mpb-tile mpb-style-btn" role="radio" data-style="' + s.id + '" aria-label="' + escapeHtml(s.title) + '">' +
        '<span class="mpb-style-thumb"><img loading="lazy" alt="" src="' + assetUrl('mpb-style-' + s.id + '.jpg') + '"></span><span class="mpb-style-name">' + escapeHtml(name) + '</span></button>';
    }).join('');
  }
  function buildMarkers() {
    ui.markerColors.innerHTML = MARKER_COLORS.map(function (c, i) {
      return '<button type="button" class="mpb-marker-color" role="radio" data-mcolor="' + i + '" aria-label="' + c.title + ' pin" title="' + c.title + '"><span style="background:' + c.color + '"></span></button>';
    }).join('');
    renderMarkerShapes();
  }
  function renderMarkerShapes() {
    ui.markers.innerHTML = MARKERS.map(function (m, i) {
      var svg = pinSvg(m, MARKER_COLORS[state.markerColor]);
      var inner = svg ? '<img alt="" src="data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg) + '">' :
        '<svg viewBox="0 0 100 120" aria-hidden="true"><circle cx="50" cy="60" r="34" fill="none" stroke="rgba(59,13,16,.35)" stroke-width="6"/><path d="M26 84 74 36" stroke="rgba(59,13,16,.35)" stroke-width="6"/></svg>';
      return '<button type="button" class="mpb-tile mpb-marker-btn" role="radio" data-marker="' + i + '" aria-label="' + MARKER_TITLES[m] + '" title="' + MARKER_TITLES[m] + '">' + inner + '</button>';
    }).join('');
    syncSelections();
  }
  function buildStands() {
    var thumbs = CFG.standThumbs || {};
    ui.stands.innerHTML = (CFG.stands || []).map(function (s) {
      var price = s.key === 'none' ? 'Free' : '+' + money(s.price);
      return '<button type="button" class="mpb-tile mpb-stand-btn" role="radio" data-stand="' + s.key + '"' + (s.key !== 'none' && !s.variantId ? ' disabled' : '') + '>' + chip(s.badge) +
        (thumbs[s.key] ? '<img alt="" loading="lazy" src="' + escapeHtml(thumbs[s.key]) + '">' : '<span class="mpb-st-none"><svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><circle cx="12" cy="12" r="8.5"/><path d="M6 18 18 6"/></svg></span>') + '<span class="mpb-st-l">' + escapeHtml(s.key === 'none' ? s.label : s.label + ' stand') + '</span>' +
        '<span class="mpb-st-p">' + price + '</span></button>';
    }).join('');
  }
  function buildFonts() {
    $('ul', ui.fonts).innerHTML = FONTS.map(function (f) { return '<li role="option" data-font="' + escapeHtml(f) + '" style="font-family:' + fontFamilyCss(f).replace(/"/g, '&quot;') + '">' + escapeHtml(f) + '</li>'; }).join('');
  }

  function syncSelections() {
    function mark(list, test) { list.forEach(function (b) { var on = test(b); b.classList.toggle('is-active', on); b.setAttribute('aria-checked', on ? 'true' : 'false'); }); }
    mark($all('.mpb-size-btn', ui.sizes), function (b) { return +b.dataset.size === state.sizeIndex; });
    mark($all('.mpb-shape-btn', ui.shapes), function (b) { return b.dataset.shape === state.shape; });
    mark($all('.mpb-style-btn', ui.styles), function (b) { return b.dataset.style === state.mapStyle; });
    mark($all('.mpb-marker-color', ui.markerColors), function (b) { return +b.dataset.mcolor === state.markerColor; });
    mark($all('.mpb-marker-btn', ui.markers), function (b) { return +b.dataset.marker === state.marker; });
    mark($all('.mpb-stand-btn', ui.stands), function (b) { return b.dataset.stand === state.stand; });
    $all('li', ui.fonts).forEach(function (li) { li.classList.toggle('is-active', li.dataset.font === state.font); });
    var fn = $('#mpb-font-name'); if (fn) { fn.textContent = state.font; fn.style.fontFamily = fontFamilyCss(state.font); }
    var sn = $('#mpb-style-name'); if (sn) sn.textContent = currentStyle().title;
    var shn = $('#mpb-shape-name'); if (shn) shn.textContent = currentShape().title;
    if (ui.media) panPreview();
    updatePrice();
  }

  // ---------------------------------------------------------------- price tally (running, itemized; total + ATC on the last step)
  function addBiz(d, n) { d = new Date(d); while (n > 0) { d.setDate(d.getDate() + 1); if (d.getDay() % 6) n--; } return d; }
  // display-currency cents, converted per line so the lines add up to the total
  function totals() {
    var s = currentSize(), st = currentStand(), showStand = maxStep >= STEPS;
    var total = D(s.price) + (showStand ? D(st.price) : 0);
    var compare = D(s.compareAt && s.compareAt > s.price ? s.compareAt : s.price) + (showStand ? D(st.compareAt && st.compareAt > st.price ? st.compareAt : st.price) : 0);
    return { total: total, compare: compare, showStand: showStand };
  }
  function updatePrice() {
    var s = currentSize(), stand = currentStand(), t = totals();
    var cmp = s.compareAt && s.compareAt > s.price ? was(s.compareAt) : '';
    var lines = '<div class="mpb-tl"><span>' + escapeHtml((s.label || '') + ' map plaque · ' + currentShape().title) + '</span><span class="mpb-p">' + money(s.price) + cmp + '</span></div>';
    var sub = [state.locationText, state.showDate && state.date ? dateText() : ''].filter(Boolean).join(' · ');
    if (sub && !state.usingDefaultLocation) lines += '<div class="mpb-tl is-sub"><span>' + escapeHtml((state.title ? '“' + state.title + '” · ' : '') + sub) + '</span></div>';
    if (t.showStand) lines += '<div class="mpb-tl' + (stand.key === 'none' ? ' is-muted' : '') + '"><span>+ ' + escapeHtml(stand.key === 'none' ? 'No stand' : stand.label + ' stand') + '</span><span class="mpb-p">' + (stand.key === 'none' ? '–' : money(stand.price)) + '</span></div>';
    else lines += '<div class="mpb-tl is-muted"><span>Stand</span><span>Step 5</span></div>';
    ui.lines.innerHTML = lines;
    ui.total.innerHTML = mD(t.total);
    ui.save.innerHTML = t.compare > t.total ? 'You save ' + mD(t.compare - t.total) : '';
    fillFromPrice();
    var n = +CFG.installments || 0;
    var logoT = $('#mpb-instlogo'), logo = logoT ? logoT.innerHTML.trim() : '';
    ui.inst.innerHTML = n > 1 ? escapeHtml(CFG.installmentsText || 'or {n} payments of {amount}').replace('{n}', n)
      .replace('{amount}', mD(Math.ceil(t.total / n))).replace('{logo}', logo).replace(/\s+$/, '') : '';
    var dims = s.dims || '';
    ui.dims.textContent = dims;
    ui.attrib.textContent = PROVIDER.attribution;
    ui.standNote.hidden = stand.key !== 'none' || !CFG.noStandNote;
    ui.standNote.textContent = CFG.noStandNote || '';
    var available = !!s.variantId;
    if (!atcBusy) ui.atc.disabled = !available;
    ui.atc.querySelector('.mpb-atc-label').innerHTML = available ? 'Add to cart · ' + mD(t.total) : 'Unavailable';
    updateSticky();
  }
  function updateDelivery() {
    var prod = parseInt(CFG.production, 10), a = parseInt(CFG.transitMin, 10), z = parseInt(CFG.transitMax, 10);
    var ok = prod >= 0 && a > 0 && z >= a;
    $('#mpb-delivery').hidden = !ok;
    var slot = $('#mpb-delivery-slot'); if (slot) slot.hidden = ok;
    if (!ok) { ui.del2.textContent = ''; return; }
    var ship = addBiz(new Date(), prod), from = addBiz(ship, a), to = addBiz(ship, z);
    var f = function (d) { return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); };
    $('#mpb-del1').textContent = f(from) + ' – ' + f(to);
    ui.del2.textContent = String(CFG.deliveryLine || '').replace('{from}', f(from)).replace('{to}', f(to));
  }

  function syncInputs() {
    ui.inTitle.value = state.title;
    ui.inLocation.value = state.locationText;
    ui.inDate.value = state.date || '';
    ui.showDate.checked = state.showDate; ui.showCoords.checked = state.showCoords;
    ui.zoom.value = state.zoom;
    ui.search.value = state.searchLabel || '';
    $('#mpb-found').hidden = state.usingDefaultLocation;
    $('#mpb-found-label').textContent = state.searchLabel || '';
    $('.mpb-search-field').classList.toggle('is-empty', state.usingDefaultLocation);
    $('#mpb-date-field').hidden = !state.showDate;
    updateCounts(); updateDateUi(); updateNextButtons(); updatePrice();
  }
  function updateCounts() {
    $('#mpb-count-title').textContent = ui.inTitle.value.length + ' / 30';
    $('#mpb-count-location').textContent = ui.inLocation.value.length + ' / 60';
  }
  function updateDateUi() {
    var d = parseISODate(state.date);
    $('#mpb-date-ph').textContent = d ? MONTHS[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear() : 'Select date';
    $('#mpb-date-ph').classList.toggle('has-value', !!d);
    ui.inDate.classList.toggle('has-value', !!d);
  }

  // ---------------------------------------------------------------- steps
  function stepBlocked(n) {
    if (n === 1) return state.usingDefaultLocation ? 'search' : '';
    if (n === 3) {
      if (!String(state.locationText || '').trim()) return 'location';
      if (state.showDate && !state.date) return 'date';
    }
    return '';
  }
  function updateNextButtons() {
    var b = $('.mpb-next[data-next="1"]'); if (b) b.setAttribute('aria-disabled', state.usingDefaultLocation ? 'true' : 'false');
    $all('.mpb-stp', ui.app).forEach(function (p) {
      var k = +p.dataset.go;
      p.classList.toggle('is-done', k !== step && k <= maxStep && (k < maxStep || maxStep === STEPS) && !stepBlocked(k));
    });
  }
  function goStep(n, opts) {
    opts = opts || {};
    n = Math.max(1, Math.min(STEPS, n));
    step = n; if (n > maxStep) maxStep = n;
    ui.app.setAttribute('data-step', n);
    $all('.mpb-step', ui.app).forEach(function (el) { el.classList.toggle('is-on', +el.dataset.step === n); });
    $all('.mpb-stp', ui.app).forEach(function (p) {
      var k = +p.dataset.go;
      p.classList.toggle('is-on', k === n);
      p.classList.toggle('is-done', k !== n && k <= maxStep && (k < maxStep || maxStep === STEPS) && !stepBlocked(k));
      p.classList.toggle('is-open', k <= maxStep && k !== n);
      p.setAttribute('aria-selected', k === n ? 'true' : 'false');
    });
    $('#mpb-stepof').textContent = 'Step ' + n + ' of ' + STEPS;
    $all('.mpb-progress span', ui.app).forEach(function (b) { b.style.width = (100 * n / STEPS) + '%'; });
    ui.reveal.classList.toggle('is-on', maxStep >= STEPS);
    updatePrice();
    panPreview();
    if (opts.scroll) scrollToBuilder();
  }
  function tryNext(from) {
    var blocked = stepBlocked(from);
    if (blocked) {
      validationActive = true; setError(blocked);
      var inp = blocked === 'search' ? ui.search : blocked === 'date' ? ui.inDate : ui.inLocation;
      if (inp) { try { inp.focus({ preventScroll: true }); } catch (e) { inp.focus(); } }
      scrollToBuilder();
      return;
    }
    goStep(from + 1, { scroll: true });
  }
  function updateSticky() {
    if (!ui.stickyBtn) return;
    var t = totals();
    ui.stickyTotal.innerHTML = mD(t.total);
    ui.stickyK.textContent = t.showStand ? 'Total' : 'Your plaque';
    var last = step >= STEPS;
    ui.stickyLabel.textContent = last ? 'Add to cart' : (step === 1 && state.usingDefaultLocation ? 'Find your place first' : NEXT_LABELS[step] + ' →');
    ui.stickyBtn.setAttribute('aria-disabled', !last && step === 1 && state.usingDefaultLocation ? 'true' : 'false');
  }
  function stickyAction() { if (step >= STEPS) addToCart(); else tryNext(step); }

  // ---------------------------------------------------------------- geocoding (Photon default, Mapbox optional)
  var GEO = {
    photon: {
      attribution: 'Search by Photon · © OpenStreetMap',
      search: function (q, signal) {
        return fetch('https://photon.komoot.io/api/?limit=6&lang=en&q=' + encodeURIComponent(q), { signal: signal })
          .then(function (r) { return r.json(); }).then(function (j) { return (j.features || []).map(photonToLoc); });
      },
      reverse: function (lat, lng) {
        return fetch('https://photon.komoot.io/reverse?lang=en&lat=' + lat + '&lon=' + lng).then(function (r) { return r.json(); })
          .then(function (j) { return j.features && j.features[0] ? photonToLoc(j.features[0]) : null; });
      }
    },
    mapbox: {
      attribution: 'Search by Mapbox',
      search: function (q, signal) {
        return fetch('https://api.mapbox.com/search/geocode/v6/forward?limit=6&q=' + encodeURIComponent(q) + '&access_token=' + encodeURIComponent(CFG.map.mapboxToken || ''), { signal: signal })
          .then(function (r) { return r.json(); }).then(function (j) { return (j.features || []).map(mapboxToLoc); });
      },
      reverse: function (lat, lng) {
        return fetch('https://api.mapbox.com/search/geocode/v6/reverse?longitude=' + lng + '&latitude=' + lat + '&access_token=' + encodeURIComponent(CFG.map.mapboxToken || ''))
          .then(function (r) { return r.json(); }).then(function (j) { return j.features && j.features[0] ? mapboxToLoc(j.features[0]) : null; });
      }
    }
  };
  var GEOCODER = GEO[CFG.geocoder] || GEO.photon;

  function photonToLoc(f) {
    var p = f.properties || {}, c = f.geometry.coordinates;
    var street = p.street ? (p.housenumber ? p.housenumber + ' ' : '') + p.street : '';
    var isPlace = p.osm_key === 'place' || p.type === 'city' || p.type === 'town' || p.type === 'village' || p.type === 'state' || p.type === 'country' || p.type === 'district' || p.type === 'county';
    var isAddress = !!p.housenumber || p.osm_key === 'highway' || p.type === 'street' || p.type === 'house';
    var name = p.name || street || '';
    var parts = [name, (name !== street ? street : ''), p.city !== name ? p.city : '', p.state !== name ? p.state : '', p.country !== name ? p.country : '']
      .filter(function (x, i, a) { return x && a.indexOf(x) === i; });
    return {
      lat: c[1], lng: c[0], title: name, label: parts.join(', '),
      city: p.city || (isPlace ? p.name : '') || p.county || '', state: p.state || '', country: p.country || '', countryCode: (p.countrycode || '').toUpperCase(),
      kind: isAddress ? 'address' : (isPlace ? 'locality' : 'poi')
    };
  }
  function mapboxToLoc(f) {
    var p = f.properties || {}, ctx = p.context || {}, c = f.geometry.coordinates;
    var type = p.feature_type;
    return {
      lat: c[1], lng: c[0], title: p.name || '', label: p.full_address || p.place_formatted || p.name || '',
      city: (ctx.place && ctx.place.name) || '', state: (ctx.region && ctx.region.name) || '', country: (ctx.country && ctx.country.name) || '',
      countryCode: (ctx.country && ctx.country.country_code || '').toUpperCase(),
      kind: type === 'address' || type === 'street' ? 'address' : (type === 'poi' ? 'poi' : 'locality')
    };
  }
  function locationTitleFor(loc) {
    // street/house/locality results -> "City, Country"; named places -> the place name.
    var cityCountry = [loc.city, loc.countryCode === 'US' ? (loc.state || loc.country) : loc.country].filter(Boolean).join(', ');
    var t = (loc.kind === 'poi' && loc.title) ? loc.title : (cityCountry || loc.title || loc.label);
    return String(t).slice(0, 60);
  }
  function parseCoords(q) {
    var m = String(q).trim().match(/^(-?\d{1,2}(?:\.\d+)?)\s*[, ]\s*(-?\d{1,3}(?:\.\d+)?)$/);
    if (!m) return null;
    var lat = +m[1], lng = +m[2];
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
    return { lat: lat, lng: lng };
  }

  var searchCtl = null, suggestions = [];
  function boldMatch(text, q) {
    var safe = escapeHtml(text), words = String(q).trim().split(/[\s,]+/).filter(function (w) { return w.length > 1; });
    words.forEach(function (w) { safe = safe.replace(new RegExp('(' + w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'ig'), '<strong>$1</strong>'); });
    return safe;
  }
  function renderSuggestions(q) {
    if (!suggestions.length) { ui.suggest.hidden = true; return; }
    ui.suggest.innerHTML = suggestions.map(function (s, i) {
      var main = s.source === 'coordinates' ? '📍 Use coordinates ' + escapeHtml(limitDp(s.lat, 6) + ', ' + limitDp(s.lng, 6))
        : (s.title && s.title !== s.label ? boldMatch(s.title, q) + ', <span class="mpb-sub">' + boldMatch(s.label.replace(s.title + ', ', ''), q) + '</span>' : boldMatch(s.label, q));
      return '<div class="mpb-suggest-item" data-idx="' + i + '"><p>' + main + '</p></div>';
    }).join('') + '<div class="mpb-suggest-attrib">' + escapeHtml(GEOCODER.attribution) + '</div>';
    ui.suggest.hidden = false;
  }
  var doSearch = debounce(function () {
    var q = ui.search.value.trim();
    if (searchCtl) searchCtl.abort();
    if (q.length < 2) { suggestions = []; renderSuggestions(q); $('#mpb-search-spin').hidden = true; return; }
    var coords = parseCoords(q);
    if (coords) { suggestions = [{ source: 'coordinates', lat: coords.lat, lng: coords.lng, label: q }]; renderSuggestions(q); return; }
    searchCtl = window.AbortController ? new AbortController() : null;
    $('#mpb-search-spin').hidden = false;
    GEOCODER.search(q, searchCtl && searchCtl.signal).then(function (list) {
      if (ui.search.value.trim() !== q) return;
      suggestions = list; renderSuggestions(q);
    }).catch(function (e) { if (e.name !== 'AbortError') console.warn('[mpb] search', e); })
      .then(function () { $('#mpb-search-spin').hidden = true; });
  }, 350);

  function selectSuggestion(s) {
    ui.suggest.hidden = true; suggestions = [];
    if (s.source === 'coordinates') {
      setLocation(s.lat, s.lng, null, limitDp(s.lat, 6) + ', ' + limitDp(s.lng, 6));
      GEOCODER.reverse(s.lat, s.lng).then(function (loc) { if (loc && !state.locationManuallyEdited) { loc.kind = 'locality'; state.locationText = locationTitleFor(loc); syncInputs(); layoutText(); } }).catch(function () {});
      return;
    }
    state.locationManuallyEdited = false;
    setLocation(s.lat, s.lng, s, s.label);
  }
  function setLocation(lat, lng, loc, searchLabel) {
    state.lat = +lat; state.lng = +lng;
    state.usingDefaultLocation = false;
    if (searchLabel != null) state.searchLabel = searchLabel;
    if (loc && !state.locationManuallyEdited) state.locationText = locationTitleFor(loc);
    clearError('search'); clearError('location');
    syncInputs(); layoutText(); schedulePreview(true);
  }

  // ---------------------------------------------------------------- pin adjust modal
  var pinMap = null, pinMarker = null, pinStart = null;
  function openPinModal() {
    if (state.usingDefaultLocation) return;
    pinStart = { lat: state.lat, lng: state.lng };
    openModal('#mpb-modal-pin');
    ensureLib().then(function () { return PROVIDER.style(state.mapStyle); }).then(function (style) {
      var GL = PROVIDER.lib();
      pinMap = new GL.Map({ container: 'mpb-pin-map', style: style, center: [state.lng, state.lat], zoom: Math.max(state.zoom + ZOOM_OFFSET, 15), attributionControl: true });
      pinMap.addControl(new GL.NavigationControl({ showCompass: false }));
      var el = document.createElement('img');
      var svg = pinSvg(MARKERS[state.marker] === 'none' ? 'pin' : MARKERS[state.marker], MARKER_COLORS[state.markerColor]);
      el.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg); el.width = 40; el.height = 48; el.className = 'mpb-drag-pin';
      pinMarker = new GL.Marker({ element: el, draggable: true, anchor: MARKERS[state.marker] === 'heart' ? 'center' : 'bottom' }).setLngLat([state.lng, state.lat]).addTo(pinMap);
    }).catch(function (e) { console.error('[mpb] pin map', e); });
  }
  function closePinModal() {
    closeModal('#mpb-modal-pin');
    if (pinMap) { pinMap.remove(); pinMap = null; pinMarker = null; }
  }
  function haversine(a, b) {
    var R = 6371000, toR = Math.PI / 180, dLat = (b.lat - a.lat) * toR, dLng = (b.lng - a.lng) * toR;
    var h = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(a.lat * toR) * Math.cos(b.lat * toR) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * R * Math.asin(Math.sqrt(h));
  }
  function confirmPin() {
    if (!pinMarker) return closePinModal();
    var ll = pinMarker.getLngLat(), next = { lat: ll.lat, lng: ll.lng };
    if (haversine(pinStart, next) < 5) return closePinModal();
    var btn = $('#mpb-pin-confirm'); btn.disabled = true;
    GEOCODER.reverse(next.lat, next.lng).catch(function () { return null; }).then(function (loc) {
      btn.disabled = false;
      state.lat = next.lat; state.lng = next.lng;
      if (loc) { state.searchLabel = loc.label; if (!state.locationManuallyEdited) { loc.kind = loc.kind === 'poi' ? 'poi' : 'locality'; state.locationText = locationTitleFor(loc); } }
      else state.searchLabel = limitDp(next.lat, 6) + ', ' + limitDp(next.lng, 6);
      closePinModal(); syncInputs(); layoutText(); schedulePreview(true);
    });
  }

  // ---------------------------------------------------------------- modals
  // Phones: a full-screen look at the plaque (mockup photo + the live design), so the small words can be read.
  // Drawn from the layers already on screen; nothing is uploaded or re-rendered.
  function openZoom() {
    var c = $('#mpb-zoom-img'); if (!c || !fabricCanvas) return;
    var entry = (manifest && manifest.stands || {})[state.stand] || {};
    var stage = entry.stage || { w: CANVAS_W, h: CANVAS_H, plaque: { x: 0, y: 0, w: CANVAS_W, h: CANVAS_H } };
    var pl = stage.plaque || { x: 0, y: 0, w: stage.w, h: stage.h };
    var crop = state.stand === 'none' ? { x: 60, y: 50, w: 880, h: 1260 } : { x: 40, y: 50, w: 920, h: 1290 };
    var k = 1.5;
    c.width = Math.round(crop.w * k); c.height = Math.round(crop.h * k);
    var ctx = c.getContext('2d');
    ctx.fillStyle = '#FCF5EB'; ctx.fillRect(0, 0, c.width, c.height);
    var put = function (img, x, y, w, h) { try { if (img && !img.hidden && img.complete !== false) ctx.drawImage(img, (x - crop.x) * k, (y - crop.y) * k, w * k, h * k); } catch (e) {} };
    put(ui.back, 0, 0, stage.w, stage.h);
    put(fabricCanvas.lowerCanvasEl || $('#mpb-canvas'), pl.x, pl.y, pl.w, pl.h);
    put(ui.front, 0, 0, stage.w, stage.h);
    openModal('#mpb-modal-zoom');
    onScroll();
  }
  function openModal(sel) { var m = $(sel); m.hidden = false; document.body.classList.add('mpb-modal-open'); }
  function closeModal(sel) { var m = $(sel); if (m) m.hidden = true; if (!$all('.mpb-modal').some(function (x) { return !x.hidden; })) document.body.classList.remove('mpb-modal-open'); if (ui.media) requestAnimationFrame(onScroll); }

  // ---------------------------------------------------------------- validation
  function setError(id) { if (errorFields.indexOf(id) === -1) errorFields.push(id); var f = $('[data-required="' + id + '"]'); if (f) f.classList.add('is-error'); }
  function clearError(id) { errorFields = errorFields.filter(function (x) { return x !== id; }); var f = $('[data-required="' + id + '"]'); if (f) f.classList.remove('is-error'); }
  function validate() {
    ['search', 'location', 'date'].forEach(clearError);
    var ok = true;
    if (state.usingDefaultLocation) { setError('search'); setError('location'); ok = false; }
    if (!String(state.locationText || '').trim()) { setError('location'); ok = false; }
    if (state.showDate && !state.date) { setError('date'); ok = false; }
    return ok;
  }

  // ---------------------------------------------------------------- export + upload + cart
  function printMultiplier() { var s = currentSize(); return (Number(s.printWidth) || 1748) / CANVAS_W; }

  // ---- order assets (flag-gated). OFF unless window.CGC_ORDER_ASSETS =
  // { enabled: true, endpoint } is set or the URL has ?cgc_assets=1. ON: cgc-order-assets.js (shared module, loaded on
  // demand) makes a 600 px cart thumbnail + the HD print PNG (iOS-safe size), saves localStorage['cgc_preview:'+id], uploads
  // when an endpoint exists and adds the contract properties (_design_id, _preview, _render, _render_thumbnail).
  // Print-file upload: section settings "Print file upload" (MPB_CONFIG.upload = { enabled, pubKey, shopId, uploadUrl, apiUrl,
  // filesUrl }). Off = no upload: the placeholder path, and the line still carries _design for a re-render.
  function uploadOn() { var u = CFG.upload || {}; return !!(u.enabled && u.pubKey && u.shopId && u.uploadUrl && u.filesUrl); }
  function orderAssetsOn() { return !!((window.CGC_ORDER_ASSETS && window.CGC_ORDER_ASSETS.enabled) || uploadOn() || /[?&]cgc_assets=1\b/.test(location.search)); }
  var orderAssetsLib = null;
  function loadOrderAssets() {
    if (!orderAssetsOn()) return Promise.resolve(null);
    if (!window.CGC_ORDER_ASSETS && uploadOn()) {
      // print first, then the cart picture; each retried once. Add to cart waits for this (UPLOAD_DEADLINE_MS cap).
      window.CGC_ORDER_ASSETS = { enabled: true, provider: 'uploadkit', uploadkit: CFG.upload, retries: 1, thumbTimeoutMs: 15000, timeoutMs: 45000 };
    }
    if (window.CGCOrderAssets) return Promise.resolve(window.CGCOrderAssets);
    if (!orderAssetsLib) orderAssetsLib = new Promise(function (res) {
      var s = document.createElement('script'); s.async = true;
      s.src = CFG.orderAssetsUrl || (assetBase + 'cgc-order-assets.js');
      s.onload = function () { res(window.CGCOrderAssets || null); };
      s.onerror = function () { console.warn('[mpb] cgc-order-assets.js did not load; legacy export'); res(null); };
      document.head.appendChild(s);
    });
    return orderAssetsLib;
  }
  // Print map with fallbacks, so add to cart still works if the full-size render fails: full size -> a smaller render
  // (~60%, still >= ~180 dpi at A3) -> the on-screen preview map scaled up. The cart line records which one was used
  // (_render_quality) so the order can be re-rendered from its saved map settings if it isn't 'full'.
  function withDeadline(p, ms, what) {
    return Promise.race([p, new Promise(function (res, rej) { setTimeout(function () { rej(new Error(what + ' timeout')); }, ms); })]);
  }
  function printMap(mult) {
    return withDeadline(renderMapCanvas(mult, { print: true }), 50000, 'print map').then(function (c) { c.dataset.quality = 'full'; return c; })
      .catch(function (err) {
        console.warn('[mpb] full-size print map failed, trying smaller', err);
        var m2 = Math.max(1, mult * 0.6);
        return withDeadline(renderMapCanvas(m2, { print: true, loadMs: 15000, idleMs: 15000 }), 32000, 'small print map').then(function (c) {
          var big = copyCanvas(c, Math.round(c.width * mult / m2), Math.round(c.height * mult / m2));
          big.dataset.glw = c.dataset.glw; big.dataset.glh = c.dataset.glh; big.dataset.quality = 'reduced-' + Math.round(300 * m2 / mult) + 'dpi';
          return big;
        });
      }).catch(function (err) {
        console.warn('[mpb] print map failed, using the preview map', err);
        if (!lastMapCanvas) throw err;
        var shape = currentShape(), c = copyCanvas(lastMapCanvas, Math.round(shape.map.w * mult), Math.round(shape.map.h * mult));
        c.dataset.glw = lastMapCanvas.dataset.glw || '?'; c.dataset.glh = lastMapCanvas.dataset.glh || '?'; c.dataset.quality = 'preview';
        return c;
      });
  }
  function renderPrintImages() {
    return withDeadline(ensureFont(state.font), 10000, 'font').catch(function () {}).then(function () { layoutText(); return loadOrderAssets(); })
      .then(function (OA) { return OA ? renderOrderAssets(OA) : renderPrintImagesLegacy(); });
  }
  // Flag ON: print PNG at 300 dpi, capped to the iOS Safari canvas limit (16.7 MP: A3 3508x4911 is 17.2 MP and comes
  // back blank on iPhones, so A3 exports at ~293 dpi; A5/A4 are untouched). toCanvasElement + toBlob = no giant base64 string.
  // Only iOS/iPadOS WebKit has the 16.7 MP canvas cap; everywhere else the print is the full 300 dpi size.
  function canvasCapped() { var ua = navigator.userAgent || ''; return /iP(hone|ad|od)/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1); }
  function renderOrderAssets(OA) {
    var want = printMultiplier(), mult = canvasCapped() ? OA.safeScale(CANVAS_W, CANVAS_H, want) : want;
    return printMap(mult).then(function (hi) {
      var prevEl = mapObj.getElement();
      placeMapObject(hi, mult);
      layoutText();
      fabricCanvas.renderAll();
      var big = typeof fabricCanvas.toCanvasElement === 'function' ? fabricCanvas.toCanvasElement(mult) : null;
      var printP = big ? OA.canvasToBlob(big, 'image/png') : dataUrlToBlob(fabricCanvas.toDataURL({ format: 'png', multiplier: mult }));
      var designUrl = fabricCanvas.toDataURL({ format: 'png', multiplier: 0.6 });
      placeMapObject(prevEl, 1);
      fabricCanvas.renderAll();
      return Promise.all([printP, composeThumb(designUrl, 600)]).then(function (r) {
        if (big) { big.width = 0; big.height = 0; }            // free the ~70 MB canvas now (iOS memory)
        return dataUrlToBlob(r[1]).then(function (thumbBlob) {
          return { print: r[0], printWidth: Math.round(CANVAS_W * mult), printHeight: Math.round(CANVAS_H * mult),
            dpi: Math.round(300 * mult / want), mapPixels: (hi.dataset.glw || '?') + 'x' + (hi.dataset.glh || '?'), quality: hi.dataset.quality,
            thumbDataUrl: r[1], thumbBlob: thumbBlob, designDataUrl: designUrl, orderAssets: true };
        });
      });
    });
  }

  // Renders the print PNG at the selected size's 300-dpi width (canvas is 1000x1400, so height = width*1.4).
  function renderPrintImagesLegacy() {
    var mult = printMultiplier();
    return printMap(mult).then(function (hi) {
      var prevEl = mapObj.getElement();
      placeMapObject(hi, mult);
      layoutText();
      fabricCanvas.renderAll();
      var printUrl = fabricCanvas.toDataURL({ format: 'png', multiplier: mult });
      var thumbUrl = fabricCanvas.toDataURL({ format: 'png', multiplier: 0.3 });
      placeMapObject(prevEl, 1);
      fabricCanvas.renderAll();
      return Promise.all([dataUrlToBlob(printUrl), composeThumb(thumbUrl)]).then(function (r) {
        return { print: r[0], printWidth: Math.round(CANVAS_W * mult), printHeight: Math.round(CANVAS_H * mult),
          mapPixels: (hi.dataset.glw || '?') + 'x' + (hi.dataset.glh || '?'), quality: hi.dataset.quality, thumbDataUrl: r[1] };
      });
    });
  }
  function dataUrlToBlob(u) { return fetch(u).then(function (r) { return r.blob(); }); }
  function composeThumb(designThumbUrl, width) {
    // Mockup thumbnail: back layer + design + front layer (what the shopper saw).
    var entry = (manifest.stands || {})[state.stand] || {};
    var stage = entry.stage || { w: CANVAS_W, h: CANVAS_H, plaque: { x: 0, y: 0, w: CANVAS_W, h: CANVAS_H } };
    var k = (width || 360) / stage.w, c = document.createElement('canvas');
    c.width = Math.round(stage.w * k); c.height = Math.round(stage.h * k);
    var ctx = c.getContext('2d');
    return Promise.all([entry.back ? loadImage(assetUrl(entry.back)).catch(function () { return null; }) : null, loadImage(designThumbUrl),
      entry.front ? loadImage(assetUrl(entry.front)).catch(function () { return null; }) : null]).then(function (imgs) {
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, c.width, c.height);
      if (imgs[0]) ctx.drawImage(imgs[0], 0, 0, c.width, c.height);
      var pl = stage.plaque; ctx.drawImage(imgs[1], pl.x * k, pl.y * k, pl.w * k, pl.h * k);
      if (imgs[2]) ctx.drawImage(imgs[2], 0, 0, c.width, c.height);
      return c.toDataURL('image/jpeg', 0.82);
    });
  }

  /**
   * uploadDesign(blobs) -> Promise<{ render, render_thumbnail, render_id }>
   * With an upload endpoint: the order-assets path posts the thumbnail + print PNG and returns public URLs.
   * Without one: keeps the mockup thumbnail as a data URL in localStorage and returns placeholder URLs.
   */
  function uploadDesign(blobs) {
    if (blobs.orderAssets && window.CGCOrderAssets) {
      var OA = window.CGCOrderAssets, did = OA.newDesignId('mpb');
      var saved = OA.saveLocalPreview(did, blobs.thumbDataUrl);   // Promise; the cart's offline thumbnail
      window.MPB_LAST_EXPORT = { id: did, blob: blobs.print, url: URL.createObjectURL(blobs.print), width: blobs.printWidth,
        height: blobs.printHeight, dpi: blobs.dpi, bytes: blobs.print.size, mapPixels: blobs.mapPixels,
        thumb: blobs.thumbBlob, thumbBytes: blobs.thumbBlob && blobs.thumbBlob.size, thumbDataUrl: blobs.thumbDataUrl, designDataUrl: blobs.designDataUrl };
      return saved.then(function () { return OA.upload({ designId: did, product: 'map-plaque', filePrefix: 'plaque', files: { thumb: blobs.thumbBlob, render: blobs.print }, meta: snapshot() }); })
        .then(function (up) { up.render_id = up.render_uuid || did; up.render_px = blobs.printWidth + 'x' + blobs.printHeight; up.render_dpi = blobs.dpi; up.render_quality = blobs.quality; return up; });
    }
    var id = 'mpbd-' + Date.now().toString(36) + '-' + makeId(6);
    try { localStorage.setItem('mpb_render_' + id, blobs.thumbDataUrl); } catch (e) { console.warn('[mpb] thumbnail not stored', e); }
    window.MPB_LAST_EXPORT = { id: id, blob: blobs.print, url: URL.createObjectURL(blobs.print), width: blobs.printWidth,
      height: blobs.printHeight, bytes: blobs.print.size, mapPixels: blobs.mapPixels };
    return Promise.resolve({
      render: 'pending-upload://' + id + '/print.png',
      render_thumbnail: 'pending-upload://' + id + '/thumb.jpg',
      render_id: id,
      render_quality: blobs.quality
    });
  }

  // Never lets an upload problem block add to cart: on any failure or after UPLOAD_DEADLINE_MS the line gets the
  // placeholder render URL + _render_status, and the order is re-rendered from _design (tools/render_from_order.py).
  var UPLOAD_DEADLINE_MS = 120000;
  function uploadDesignSafe(blobs) {
    return withDeadline(uploadDesign(blobs), UPLOAD_DEADLINE_MS, 'upload').catch(function (err) {
      console.warn('[mpb] upload failed; the line carries _design for a re-render', err);
      var ex = window.MPB_LAST_EXPORT, id = (ex && ex.id) || ('mpbd-' + Date.now().toString(36) + '-' + makeId(6));
      var up = { render: 'pending-upload://' + id + '/print.png', render_thumbnail: 'pending-upload://' + id + '/thumb.jpg', render_id: id,
        render_quality: blobs.quality, status: 'failed' };
      if (blobs.orderAssets && window.CGCOrderAssets) { up.design_id = id; up.render = 'pending-upload://' + id + '/render.png'; up.render_thumbnail = ''; }
      return up;
    });
  }

  /* _design: the whole design in one compact hidden line property (well under 1 KB), so ANY order can be re-rendered
     exactly (tools/render_from_order.py opens the builder with ?mpb_design=<this JSON>). Keys:
     v format, bv builder version, p searched place, la/ln center, z zoom (slider value; the map renders at z+1), st map style id,
     sh shape, sz size label, pw print width px, sd stand, t title, l location line, d date (yyyy-mm-dd or null),
     df date format, sdt show date, sc show coordinates, f font, m marker shape, mc marker color id, mp map provider.
     The map is always north-up (no bearing/pitch in this builder); text is always black. */
  var DESIGN_FORMAT = 'mpb1';
  function designJson() {
    var size = currentSize();
    return JSON.stringify({ v: DESIGN_FORMAT, bv: CFG.version || '', la: state.lat, ln: state.lng, z: state.zoom, st: state.mapStyle,
      p: state.searchLabel || '', sh: state.shape, sz: size.label || '', pw: Number(size.printWidth) || 0, sd: state.stand, t: state.title, l: state.locationText,
      d: state.date || null, df: state.dateFormat, sdt: state.showDate ? 1 : 0, sc: state.showCoords ? 1 : 0, f: state.font,
      m: MARKERS[state.marker], mc: MARKER_COLORS[state.markerColor].id, mp: (CFG.map && CFG.map.provider) || 'openfreemap' });
  }
  function applyDesignJson(raw) {
    var d = typeof raw === 'string' ? parseJSON(raw) : raw;
    if (!d || d.v !== DESIGN_FORMAT || !isFinite(d.la) || !isFinite(d.ln)) return false;
    var sizes = CFG.sizes || [], si = -1;
    sizes.forEach(function (x, i) { if (si < 0 && x.label === d.sz) si = i; });
    sizes.forEach(function (x, i) { if (si < 0 && Number(x.printWidth) === Number(d.pw)) si = i; });
    var mi = MARKERS.indexOf(d.m), ci = -1;
    MARKER_COLORS.forEach(function (c, i) { if (c.id === d.mc) ci = i; });
    state.lat = Number(d.la); state.lng = Number(d.ln); state.zoom = Number(d.z) || state.zoom;
    if (MAP_STYLES.some(function (x) { return x.id === d.st; })) state.mapStyle = d.st;
    if (SHAPES.some(function (x) { return x.id === d.sh; })) state.shape = d.sh;
    if (si >= 0) state.sizeIndex = si;
    if ((CFG.stands || []).some(function (x) { return x.key === d.sd; })) state.stand = d.sd;
    state.title = String(d.t == null ? '' : d.t); state.locationText = String(d.l == null ? '' : d.l);
    state.date = d.d || null; if (DATE_FORMATS.indexOf(d.df) >= 0) state.dateFormat = d.df;
    state.showDate = !!d.sdt; state.showCoords = !!d.sc;
    if (FONTS.indexOf(d.f) >= 0) state.font = d.f;
    if (mi >= 0) state.marker = mi; if (ci >= 0) state.markerColor = ci;
    state.usingDefaultLocation = false; state.locationManuallyEdited = true; state.searchLabel = String(d.p || state.locationText);
    return true;
  }
  function snapshot() { var s = clone(state); s.v = 1; return s; }
  function saveDesign(key) { try { localStorage.setItem('mpb_design_' + key, JSON.stringify(snapshot())); return true; } catch (e) { return false; } }
  function restoreDesign(key) {
    try {
      var s = JSON.parse(localStorage.getItem('mpb_design_' + key) || 'null');
      if (!s) return false;
      Object.keys(state).forEach(function (k) { if (s[k] !== undefined) state[k] = s[k]; });
      return true;
    } catch (e) { return false; }
  }

  function buildCartPayload(up, designKey) {
    var size = currentSize(), stand = currentStand(), entry = (manifest.stands || {})[state.stand] || {};
    // Readable properties first: they are what the team reads on the order line (and the shopper sees in cart/checkout).
    var printed = textEntries(), mk = MARKERS[state.marker];
    var props = {};
    if (/^https:/.test(up.render || '')) props['Print file'] = up.render;
    props['Size'] = (size.label || '') + (size.short ? ' (' + size.short + ')' : '');
    props['Shape'] = currentShape().title;
    props['Map style'] = currentStyle().title;
    props['Place'] = state.searchLabel || state.locationText;
    props['Coordinates'] = Number(state.lat).toFixed(6) + ', ' + Number(state.lng).toFixed(6);
    props['Title'] = state.title || '(blank)';
    props['Place line'] = (printed.location || printed.locationDate || {}).text || '(blank)';
    props['Date line'] = state.showDate ? dateText() + (printed.locationDate ? ' (on the place line)' : '') : 'Not shown';
    props['Coordinates line'] = state.showCoords ? coordsText() : 'Not shown';
    props['Pin'] = mk === 'none' ? 'None' : MARKER_TITLES[mk] + ', ' + MARKER_COLORS[state.markerColor].title;
    props['Font'] = state.font;
    props['Stand'] = stand.label;
    var hidden = {
      '_render': up.render,
      '_render_thumbnail': up.render_thumbnail,
      '_render_id': up.render_id,
      '_design_key': designKey,
      '_editor': 'mpb',
      '_edit_path': window.location.pathname + '?design=' + designKey,
      '_fork': state.shape,
      '_product_canvas_bg': assetUrl(entry.back),
      '_product_canvas_bg_mask': assetUrl(entry.front),
      '_map_center': state.lat + ',' + state.lng,
      '_map_zoom': String(state.zoom),
      '_map_style_id': state.mapStyle,
      '_map_provider': (CFG.map && CFG.map.provider) || 'openfreemap',
      '_marker': MARKERS[state.marker] + '/' + MARKER_COLORS[state.markerColor].id,
      '_font': state.font,
      '_show_coordinates': state.showCoords ? 'yes' : 'no',
      '_print_px': Math.round(CANVAS_W * printMultiplier()) + 'x' + Math.round(CANVAS_H * printMultiplier()),
      '_render_quality': up.render_quality || 'full',
      '_added_at': String(Date.now())
    };
    Object.keys(hidden).forEach(function (k) { props[k] = hidden[k]; });
    if (up.design_id && window.CGCOrderAssets) {         // order-assets flag: contract properties replace the placeholder URLs
      delete props._render; delete props._render_thumbnail;
      var extra = window.CGCOrderAssets.props(up, { _render_id: up.render_id, _render_px: up.render_px, _render_dpi: up.render_dpi });
      Object.keys(extra).forEach(function (k) { props[k] = extra[k]; });
    } else {
      props._render_status = up.status || 'not-uploaded';
    }
    if (up.status === 'failed' || (uploadOn() && !/^https:/.test(up.render || ''))) props._upload_failed = '1';
    props._design = designJson();
    var items = [];
    if (stand.key !== 'none' && stand.variantId) items.push({ id: stand.variantId, quantity: 1, properties: up.design_id
      ? { _atc_source: 'map-plaque', _design_key: designKey, _design_id: up.design_id } : { _atc_source: 'map-plaque', _design_key: designKey } });
    items.push({ id: size.variantId, quantity: 1, properties: props });
    return { items: items };
  }

  var atcBusy = false;
  function addToCart() {
    if (atcBusy) return;
    validationActive = true;
    if (!validate()) { goStep(stepBlocked(1) ? 1 : 3, { scroll: true }); return; }
    atcBusy = true; setAtcLoading(true);
    $('#mpb-saving').hidden = false; $('#mpb-success').hidden = true; $('#mpb-error').hidden = true;
    setProgress(8); openModal('#mpb-modal-saving');
    var designKey = makeId(10);
    var saveFailed = function (err) { err = err instanceof Error ? err : new Error(String(err)); err.mpbSave = true; throw err; };
    withDeadline(readyPromise, 30000, 'preview').then(function () { setProgress(20); return withDeadline(renderPrintImages(), 100000, 'save'); }).then(function (blobs) {
      setProgress(70);
      return uploadDesignSafe(blobs).then(function (up) { return { up: up, blobs: blobs }; });
    }, saveFailed).then(function (r) {
      saveDesign(designKey);
      var payload = buildCartPayload(r.up, designKey);
      window.MPB_LAST_PAYLOAD = payload;
      setProgress(88);
      return fetch((window.Shopify && Shopify.routes && Shopify.routes.root || '/') + 'cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload)
      }).then(function (res) {
        if (!res.ok) return res.json().catch(function () { return {}; }).then(function (j) { throw new Error(j.description || j.message || ('Cart add failed: ' + res.status)); });
        return res.json();
      }).then(function (json) {
        window.MPB_LAST_CART_RESPONSE = json;
        setProgress(100);
        $('#mpb-success-thumb').src = r.blobs.thumbDataUrl;
        $('#mpb-saving').hidden = true; $('#mpb-success').hidden = false;
        refreshCartCount();
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: json } }));
        document.dispatchEvent(new CustomEvent('cart:build'));
        // If the store's cart drawer slides in, let it be the confirmation: one overlay, not two.
        // Checked by what sits on top at the right edge: normally our modal backdrop; a drawer above it means the cart is open.
        [900, 1800].forEach(function (ms) {
          setTimeout(function () {
            var top = document.elementFromPoint(window.innerWidth - 30, Math.round(window.innerHeight / 2));
            if (top && !top.closest('.mpb') && !$('#mpb-modal-saving').hidden) closeModal('#mpb-modal-saving');
          }, ms);
        });
      });
    }).catch(function (err) {
      console.error('[mpb] add to cart', err);
      $('#mpb-saving').hidden = true; $('#mpb-error').hidden = false;
      // plain words for the shopper; the technical reason stays in the console
      $('#mpb-error-msg').textContent = err && err.mpbSave
        ? 'Your design didn’t finish saving. Nothing was added to your cart. Please tap Try again.'
        : 'Your plaque wasn’t added to your cart' + (err && err.message && !/fetch|network|timeout|failed: \d{3}|unavailable/i.test(err.message) ? ' (' + err.message + ')' : '') + '. Please tap Try again.';
      // the store's cart drawer may open on a /cart/add call and would cover this message
      [500, 1300].forEach(function (ms) { setTimeout(closeCartDrawerOverUs, ms); });
    }).then(function () { atcBusy = false; setAtcLoading(false); });
  }
  function closeCartDrawerOverUs() {
    if ($('#mpb-error').hidden || $('#mpb-modal-saving').hidden) return;
    var top = document.elementFromPoint(window.innerWidth - 30, Math.round(window.innerHeight / 2));
    if (!top || top.closest('.mpb')) return;
    if (window.CGCCart && window.CGCCart.close) { window.CGCCart.close(); return; }   /* the theme's cart drawer (assets/cgc-drawer.js) */
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
  }
  function setProgress(p) { $('#mpb-saving-fill').style.transform = 'scaleX(' + (p / 100) + ')'; }
  function setAtcLoading(on) { ui.atc.querySelector('.mpb-atc-label').hidden = on; ui.atc.querySelector('.mpb-ring').hidden = !on; ui.atc.disabled = on; }
  function refreshCartCount() {
    fetch((window.Shopify && Shopify.routes && Shopify.routes.root || '/') + 'cart.js').then(function (r) { return r.json(); }).then(function (c) {
      ui.cartCount.textContent = c.item_count; ui.cartBtn.hidden = !(c.item_count > 0);
    }).catch(function () {});
  }

  // ---------------------------------------------------------------- layout helpers: header offset, pinned preview, scrolling
  function isStacked() { return window.matchMedia('(max-width: 989px)').matches; }
  // Height of whatever the theme pins to the top (sticky header, sale bar). Found by probing the top of the viewport,
  // so it keeps working when the header/countdown sections change.
  function headerOffset() {
    var y = 1, guard = 0, pinned;
    do {
      pinned = null;
      var els = document.elementsFromPoint ? document.elementsFromPoint(Math.round(window.innerWidth / 2), y) : [];
      for (var i = 0; i < els.length && !pinned; i++) {
        for (var el = els[i]; el && el !== document.body && el !== document.documentElement; el = el.parentElement) {
          if (el.closest('.mpb') || el.id === 'PBarNextFrameWrapper' || el.id === 'preview-bar-iframe') break;
          var pos = getComputedStyle(el).position;
          if (pos === 'fixed' || pos === 'sticky') {
            var r = el.getBoundingClientRect();
            if (r.top <= y && r.bottom > y && r.height < window.innerHeight * 0.35 && r.width >= window.innerWidth * 0.6) pinned = r;
            break;
          }
        }
      }
      if (pinned) y = Math.ceil(pinned.bottom) + 1;
    } while (pinned && ++guard < 4);
    return Math.max(0, y - 1);
  }
  var hdrPx = -1, hdrMax = 0;
  function syncHeaderOffset() {
    var h = headerOffset();
    if (h !== hdrPx) { hdrPx = h; ui.app.style.setProperty('--mpb-hdr', h + 'px'); }
    if (h > hdrMax && h < 200) hdrMax = h;
  }
  // pinned = what stays on screen once stuck (the media's top --mpb-collapse px tuck under the header on phones)
  function pinnedPreviewHeight() { return isStacked() ? ui.media.getBoundingClientRect().height - fitD : 0; }

  // ---------------------------------------------------------------- phone preview: full-width window onto the mockup
  // Top of page: the window is tall (up to --mpb-pvtop) and shows the whole plaque. Scrolling tucks the top of the media
  // under the header (sticky top = header - collapse), which shrinks the window to --mpb-pvh; meanwhile the plaque grows
  // to the full width and pans to what the open step is about. Only transforms the stage box, never the page layout.
  var GLASS_X0 = 100, GLASS_X1 = 895, fitD = 0, fitProbes = null, panTimer = 0;
  function previewRegions() {
    var entry = manifest && (manifest.stands || {})[state.stand] || {};
    var pl = (entry.stage && entry.stage.plaque) || { x: 169, y: 192.6, w: 662, h: 926.8 };
    var k = pl.h / CANVAS_H, sh = currentShape(), focus = null, alt = null, cut = null, cutA = null;
    var ys = Object.keys(sh.text).map(function (n) { return sh.text[n]; });
    var Y = function (v) { return pl.y + v * k; };
    if (step <= 2) {
      // the map, plus the date + title under it when there is room; never stop the window halfway through a line
      focus = [Y(sh.map.y - 20), Y((sh.text.title || sh.map.y + sh.map.h) + 100)];
      alt = [Y(sh.map.y - 20), Y(sh.map.y + sh.map.h + 20)];
      cut = Y(Math.min.apply(null, ys) - 6);
      var after = ys.filter(function (v) { return v > (sh.text.title || 0); });
      cutA = after.length ? Y(Math.min.apply(null, after) - 4) : null;
    } else if (step === 3) focus = [Y(Math.min.apply(null, ys) - 30), Y(Math.max.apply(null, ys) + 70)];
    return { y0: 60, y1: state.stand === 'none' ? 1295 : 1330, focus: focus, alt: alt, cut: cut, cutA: cutA };
  }
  function fitPreview() {
    var pv = ui.pv, wrap = ui.stageWrap, st = ui.stage;
    if (!pv || !wrap || !st) return;
    if (!isStacked() || ui.app.classList.contains('is-collapsed')) {
      if (pv.classList.contains('is-fit') || fitD) {
        pv.classList.remove('is-fit'); pv.style.height = ''; wrap.style.height = '';
        st.style.width = st.style.marginLeft = st.style.marginTop = '';
        ui.media.style.removeProperty('--mpb-collapse'); ui.media.style.removeProperty('--mpb-hidden'); fitD = 0;
      }
      return;
    }
    if (!fitProbes) {
      fitProbes = ['--mpb-pvh', '--mpb-pvtop'].map(function (v) {
        var d = document.createElement('div'); d.setAttribute('aria-hidden', 'true');
        d.style.cssText = 'position:absolute;left:0;top:0;width:0;visibility:hidden;pointer-events:none;height:var(' + v + ')';
        pv.appendChild(d); return d;
      });
    }
    var W = pv.clientWidth; if (!W) return;
    var hPin = fitProbes[0].offsetHeight, reg = previewRegions(), wholeH = reg.y1 - reg.y0;
    var sW = (W - 16) / (GLASS_X1 - GLASS_X0);                       // plaque glass = full width minus 8px gutters
    var hTop = Math.max(hPin, Math.min(fitProbes[1].offsetHeight, Math.round(wholeH * sW)));
    var D = hTop - hPin;
    pv.classList.add('is-fit'); pv.style.height = hTop + 'px';
    if (D !== fitD) { fitD = D; ui.media.style.setProperty('--mpb-collapse', D + 'px'); }
    var hidden = Math.round(Math.max(0, Math.min(D, (hdrPx > 0 ? hdrPx : 0) - (ui.media.getBoundingClientRect().top - relShift))));
    var h = hTop - hidden;
    ui.media.style.setProperty('--mpb-hidden', hidden + 'px');
    wrap.style.height = h + 'px';
    var sWhole = Math.min(sW, h / wholeH), cWhole = (reg.y0 + reg.y1) / 2, s = sWhole, cy = cWhole;
    if (reg.focus) {
      var t = D > 0 ? hidden / D : 1, vis = h / sW, fc = (reg.focus[0] + reg.focus[1]) / 2;
      // too short for map + words: frame the map and end the window above the first line of text
      if (reg.alt && vis < reg.focus[1] - reg.focus[0]) fc = Math.min((reg.alt[0] + reg.alt[1]) / 2, reg.cut - vis / 2);
      else if (reg.cutA) fc = Math.min(fc, reg.cutA - vis / 2);
      var cFocus = vis >= wholeH ? cWhole : Math.max(reg.y0 + vis / 2, Math.min(reg.y1 - vis / 2, fc));
      s = sWhole + (sW - sWhole) * t; cy = cWhole + (cFocus - cWhole) * t;
    }
    st.style.width = (1000 * s).toFixed(2) + 'px';
    st.style.marginLeft = (W / 2 - (GLASS_X0 + GLASS_X1) / 2 * s).toFixed(2) + 'px';
    st.style.marginTop = (h / 2 - cy * s).toFixed(2) + 'px';
  }
  // step / stand / shape / typing changes: glide to the new framing
  function panPreview() {
    if (!ui.pv) return;
    ui.pv.classList.add('is-panning'); clearTimeout(panTimer);
    panTimer = setTimeout(function () { ui.pv.classList.remove('is-panning'); }, 450);
    fitPreview();
  }
  // Desktop: bring the builder card into view. Phones: put the open step's heading right under the pinned preview + pills.
  var scrollFixTimer = 0;
  function scrollToBuilder(isFix) {
    var el = isStacked() ? ($('.mpb-step.is-on h3', ui.app) || ui.builder) : ui.builder;
    if (!el) return;
    var pinned = (hdrPx > 0 ? hdrPx : 0) + pinnedPreviewHeight();
    var r = el.getBoundingClientRect();
    var top = r.top + window.pageYOffset - pinned - 10;
    // scrolling UP makes a hide-on-scroll header slide back in and push the pinned preview down: leave room for it
    if (top < window.pageYOffset && hdrMax > hdrPx) top -= hdrMax - hdrPx;
    if (Math.abs(window.pageYOffset - top) > 4 && (r.top < pinned + 4 || (!isFix && r.top > window.innerHeight * (isStacked() ? 0.55 : 0.6)))) {
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      // phones: a sticky header that only pins once the page scrolls isn't counted above, so check once more when the
      // scroll settles and nudge the heading out from under the pinned preview if it landed there
      if (!isFix && isStacked()) {
        var want = Math.max(0, top), settle = function () {
          clearTimeout(scrollFixTimer); window.removeEventListener('scrollend', settle);
          // skip if the shopper has scrolled on their own since
          scrollFixTimer = setTimeout(function () { if (Math.abs(window.pageYOffset - want) < 40) { syncHeaderOffset(); scrollToBuilder(true); } }, 80);
        };
        clearTimeout(scrollFixTimer);
        if ('onscrollend' in window) window.addEventListener('scrollend', settle);
        scrollFixTimer = setTimeout(settle, 1000);
      }
    }
  }
  // Phones: the pinned preview lets go once the builder card has scrolled up past it, so the cards after the builder
  // (delivery date) and the next section never sit half-hidden under it. A transform only, never the page layout.
  var relShift = 0;
  function releasePreview() {
    var want = 0;
    if (isStacked() && !ui.app.classList.contains('is-typing')) {
      var base = ui.media.getBoundingClientRect().bottom - relShift, bb = ui.builder.getBoundingClientRect().bottom;
      if (bb < base) want = Math.round(bb - base);
    }
    if (want !== relShift) { relShift = want; ui.media.style.transform = want ? 'translateY(' + want + 'px)' : ''; }
  }
  function onScroll() {
    syncHeaderOffset();
    fitPreview();
    releasePreview();
    var mr = ui.media.getBoundingClientRect();
    if (relShift) mr = { top: mr.top - relShift, bottom: mr.bottom - relShift, height: mr.height };
    // keep focused fields and scrolled-to tiles clear of the pinned preview (top) and the sticky bar (bottom)
    var de = document.documentElement;
    de.style.scrollPaddingTop = isStacked() ? Math.round(hdrPx + mr.height - fitD + 8) + 'px' : '';
    de.style.scrollPaddingBottom = isStacked() ? '84px' : '';
    ui.media.classList.toggle('is-stuck', isStacked() && mr.top <= hdrPx - fitD + 1 && window.pageYOffset > 20);
    // sticky bar: on phones, once the builder is on screen and while the in-card button is not visible
    var br = ui.builder.getBoundingClientRect();
    var ctaVisible = false;
    var cta = step >= STEPS ? ui.atc : $('.mpb-step.is-on .mpb-next', ui.app);
    if (cta) { var cr = cta.getBoundingClientRect(); ctaVisible = cr.top < window.innerHeight - 10 && cr.bottom > hdrPx + pinnedPreviewHeight(); }
    var show = isStacked() && br.top < window.innerHeight && br.bottom > hdrPx + 120 && !ctaVisible && !document.body.classList.contains('mpb-modal-open') && !ui.app.classList.contains('is-typing');
    ui.sticky.classList.toggle('is-hidden', !show);
  }
  // Phones, while a field has focus (keyboard up): the pinned preview shrinks to a strip, the step pills fold away (an
  // equal margin on the builder keeps the page from moving) and the sticky bar hides. Nothing is undone by scrolling, so
  // the page doesn't jump when the keyboard closes.
  function setTyping(on) {
    var was = ui.app.classList.contains('is-typing');
    if (on && isStacked() && !was && ui.pvSteps) {
      var ps = ui.pvSteps, h = ps.offsetHeight + (parseFloat(getComputedStyle(ps).marginTop) || 0);
      ui.app.style.setProperty('--mpb-pillsh', Math.round(h) + 'px');
    }
    ui.app.classList.toggle('is-typing', !!on && isStacked());
    if (was !== ui.app.classList.contains('is-typing')) { panPreview(); onScroll(); }
  }
  // Keep the focused field in the part of the screen the shopper can see: below the header + pinned preview strip and
  // above the keyboard (visualViewport = the area the keyboard leaves). Checked on focus, when the keyboard finishes
  // opening (visualViewport resize) and once more as a fallback for browsers without those events.
  var fieldTimers = [];
  function keepFieldVisible(el) {
    if (!isStacked() || !el || document.activeElement !== el) return;
    syncHeaderOffset();
    var vv = window.visualViewport, vTop = vv ? vv.offsetTop : 0, vH = vv ? vv.height : window.innerHeight;
    var topLimit = Math.max(vTop, (hdrPx > 0 ? hdrPx : 0) + pinnedPreviewHeight()) + 8, bottomLimit = vTop + vH - 12;
    var r = el.getBoundingClientRect(), field = el.closest('.mpb-field'), lab = field ? field.getBoundingClientRect().top : r.top;
    var room = bottomLimit - topLimit, labelPad = r.top - lab;
    if (r.top >= topLimit && r.bottom <= bottomLimit) return;
    // label + field when there's room, else the field alone at the top of the free area
    var delta = (room >= r.height + labelPad ? lab : r.top) - topLimit;
    if (Math.abs(delta) < 4) return;
    var smooth = !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    window.scrollTo({ top: Math.max(0, window.pageYOffset + delta), behavior: smooth ? 'smooth' : 'auto' });
  }
  function onFieldFocus(el) {
    fieldTimers.forEach(clearTimeout);
    requestAnimationFrame(function () { keepFieldVisible(el); });
    fieldTimers = [setTimeout(function () { keepFieldVisible(el); }, 450), setTimeout(function () { keepFieldVisible(el); }, 900)];
  }

  // ---------------------------------------------------------------- fonts
  // The font stylesheet must be IN before document.fonts.load(): asked earlier, load() resolves at once with no faces and
  // the text (preview AND print) silently renders in the fallback serif.
  var googleFontsCss = null;
  function fontsCss() {
    if (!googleFontsCss) googleFontsCss = new Promise(function (res) {
      var l = document.querySelector('link[href="' + GOOGLE_FONTS_CSS + '"]');
      if (l && l.sheet) return res();
      if (!l) { l = document.createElement('link'); l.rel = 'stylesheet'; l.href = GOOGLE_FONTS_CSS; document.head.appendChild(l); }
      l.addEventListener('load', function () { res(); }); l.addEventListener('error', function () { res(); });
      setTimeout(res, 8000);
    });
    return googleFontsCss;
  }
  function ensureFont(font) {
    if (!document.fonts || font === 'Arial') { if (font !== 'Arial') fontsCss(); return Promise.resolve(); }
    var spec = ['700 85px "' + font + '"', '400 33px "' + font + '"'];
    return fontsCss().then(function () {
      return Promise.all(spec.map(function (f) { return document.fonts.load(f); }));
    }).catch(function () {}).then(function () {
      if (!spec.every(function (f) { try { return document.fonts.check(f); } catch (e) { return true; } })) console.warn('[mpb] font not loaded', font);
      if (fabric.util.clearFabricFontCache) fabric.util.clearFabricFontCache();
    });
  }

  // ---------------------------------------------------------------- events
  function bind() {
    ui.app.addEventListener('click', function (ev) {
      var t = ev.target.closest('[data-action],[data-next],[data-go],[data-size],[data-shape],[data-style],[data-mcolor],[data-marker],[data-stand],[data-font],.mpb-suggest-item,.mpb-modal');
      if (!t) return;
      if (t.dataset.next) { tryNext(+t.dataset.next); return; }
      if (t.dataset.go) { var g = +t.dataset.go; if (g <= maxStep || t.classList.contains('mpb-back')) goStep(g, { scroll: true }); return; }
      if (t.classList.contains('mpb-modal')) { if (ev.target === t && t.id !== 'mpb-modal-saving') { t.id === 'mpb-modal-pin' ? closePinModal() : closeModal('#' + t.id); } return; }
      if (t.dataset.size != null) { if (t.disabled) return; state.sizeIndex = +t.dataset.size; syncSelections(); return; }
      if (t.dataset.shape) { state.shape = t.dataset.shape; syncSelections(); layoutText(); schedulePreview(true); return; }
      if (t.dataset.style) { state.mapStyle = t.dataset.style; syncSelections(); schedulePreview(true); return; }
      if (t.dataset.mcolor != null) { state.markerColor = +t.dataset.mcolor; renderMarkerShapes(); schedulePreview(true); return; }
      if (t.dataset.marker != null) { state.marker = +t.dataset.marker; syncSelections(); schedulePreview(true); return; }
      if (t.dataset.stand) { state.stand = t.dataset.stand; syncSelections(); applyStandLayers(); return; }
      if (t.dataset.font) { state.font = t.dataset.font; ui.fonts.hidden = true; syncSelections(); ensureFont(state.font).then(layoutText); return; }
      if (t.classList.contains('mpb-suggest-item')) {
        if (t.closest('#mpb-suggest')) selectSuggestion(suggestions[+t.dataset.idx]);
        return;
      }
      switch (t.dataset.action) {
        case 'size-guide': openModal('#mpb-modal-size'); break;
        case 'adjust-pin': openPinModal(); break;
        case 'pin-reset': if (pinMarker && pinStart) { pinMarker.setLngLat([pinStart.lng, pinStart.lat]); pinMap.easeTo({ center: [pinStart.lng, pinStart.lat] }); } break;
        case 'pin-confirm': confirmPin(); break;
        case 'close-modal': var m = t.closest('.mpb-modal'); m.id === 'mpb-modal-pin' ? closePinModal() : closeModal('#' + m.id); break;
        case 'toggle-fonts': ui.fonts.hidden = !ui.fonts.hidden; if (!ui.fonts.hidden) { ensureFont('Poppins'); var on = $('li.is-active', ui.fonts); if (on) ui.fonts.scrollTop = on.offsetTop - 60; } break;
        case 'date-format': openFormats(); break;
        case 'retry-atc': closeModal('#mpb-modal-saving'); addToCart(); break;
        case 'zoom-preview': if (isStacked()) openZoom(); break;
      }
    });

    ui.search.addEventListener('input', doSearch);
    ui.search.addEventListener('compositionend', doSearch);
    ui.search.addEventListener('focus', function () { ui.search.select(); if (suggestions.length) ui.suggest.hidden = false; });
    ui.search.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { ui.suggest.hidden = true; }
      if (e.key === 'Enter') {
        var q = ui.search.value.trim(), c = parseCoords(q);
        e.preventDefault();
        if (c) selectSuggestion({ source: 'coordinates', lat: c.lat, lng: c.lng });
        else if (suggestions.length) selectSuggestion(suggestions[0]);
      }
    });
    document.addEventListener('pointerdown', function (e) {
      if (!e.target.closest('.mpb-search-field')) ui.suggest.hidden = true;
      if (!e.target.closest('.mpb-font-outer')) ui.fonts.hidden = true;
      if (!e.target.closest('#mpb-date-field')) $('#mpb-formats').hidden = true;
    }, true);

    // Title / location: patch text on every keystroke (no map re-render)
    ui.inTitle.addEventListener('input', function () { state.title = ui.inTitle.value; updateCounts(); layoutText(); showExamples(); updatePrice(); });
    ui.inTitle.addEventListener('focus', showExamples);
    ui.inTitle.addEventListener('blur', function () { setTimeout(function () { $('#mpb-title-examples').hidden = true; }, 150); });
    $('#mpb-title-examples').addEventListener('mousedown', function (e) {
      var li = e.target.closest('li'); if (!li) return; e.preventDefault();
      state.title = li.dataset.v; ui.inTitle.value = state.title; updateCounts(); layoutText(); updatePrice(); $('#mpb-title-examples').hidden = true;
    });
    ui.inLocation.addEventListener('input', function () { state.locationText = ui.inLocation.value; state.locationManuallyEdited = true; updateCounts(); layoutText(); updatePrice(); if (validationActive && state.locationText.trim()) clearError('location'); });
    ui.inDate.addEventListener('change', function () { state.date = ui.inDate.value || null; updateDateUi(); layoutText(); updatePrice(); if (state.date) clearError('date'); });
    ui.inDate.addEventListener('input', function () { state.date = ui.inDate.value || null; updateDateUi(); layoutText(); updatePrice(); if (state.date) clearError('date'); });
    ui.showDate.addEventListener('change', function () { state.showDate = ui.showDate.checked; $('#mpb-date-field').hidden = !state.showDate; if (!state.showDate) clearError('date'); layoutText(); updatePrice(); });
    ui.showCoords.addEventListener('change', function () { state.showCoords = ui.showCoords.checked; layoutText(); });
    var zoomLive = debounce(function () { schedulePreview(true); }, 120);
    ui.zoom.addEventListener('input', function () { state.zoom = +ui.zoom.value; zoomLive(); });

    ui.atc.addEventListener('click', addToCart);
    ui.stickyBtn.addEventListener('click', stickyAction);
    ui.pvToggle.addEventListener('click', function () {
      var c = !ui.app.classList.contains('is-collapsed');
      ui.app.classList.toggle('is-collapsed', c); ui.pvToggle.setAttribute('aria-expanded', c ? 'false' : 'true');
      requestAnimationFrame(onScroll);
    });
    // phones: shrink the pinned preview while the keyboard is up, so the field and the plaque both stay in view
    var TYPING = 'input[type=text],input[type=date]';
    ui.app.addEventListener('focusin', function (e) { if (e.target.matches(TYPING)) { setTyping(true); onFieldFocus(e.target); } });
    ui.app.addEventListener('focusout', function (e) { if (e.target.matches(TYPING)) setTimeout(function () { var a = document.activeElement; if (!(a && a.matches && a.matches('.mpb input[type=text],.mpb input[type=date]'))) { fieldTimers.forEach(clearTimeout); setTyping(false); } }, 60); });
    if (window.visualViewport) window.visualViewport.addEventListener('resize', debounce(function () {
      var a = document.activeElement;
      if (a && a.matches && a.matches('.mpb input[type=text],.mpb input[type=date]')) { onScroll(); keepFieldVisible(a); }
    }, 80));
    var rafPending = false;
    window.addEventListener('scroll', function () { if (rafPending) return; rafPending = true; requestAnimationFrame(function () { rafPending = false; onScroll(); }); }, { passive: true });
    window.addEventListener('resize', debounce(function () { resizeCanvasCss(); onScroll(); }, 100));
    if (window.ResizeObserver) new ResizeObserver(function () { resizeCanvasCss(); }).observe(ui.canvasBox);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') { if (!$('#mpb-modal-pin').hidden) closePinModal(); closeModal('#mpb-modal-size'); closeModal('#mpb-modal-zoom'); } });
  }
  function showExamples() {
    var q = ui.inTitle.value.toLowerCase(), box = $('#mpb-title-examples');
    var list = TITLE_EXAMPLES.filter(function (x) { return x.toLowerCase().indexOf(q) !== -1 && x !== ui.inTitle.value; });
    if (!list.length || document.activeElement !== ui.inTitle) { box.hidden = true; return; }
    box.innerHTML = '<p class="mpb-suggest-title">Suggested</p><ul>' + list.map(function (x) { return '<li data-v="' + escapeHtml(x) + '">' + escapeHtml(x) + '</li>'; }).join('') + '</ul>';
    box.hidden = false;
  }
  function openFormats() {
    var d = parseISODate(state.date) || EXAMPLE_DATE, box = $('#mpb-formats');
    box.innerHTML = '<p class="mpb-suggest-title">Date Formats</p><ul>' + DATE_FORMATS.map(function (f) {
      return '<li data-fmt="' + f + '" class="' + (f === state.dateFormat ? 'is-active' : '') + '">' + escapeHtml(formatDate(d, f)) + '</li>';
    }).join('') + '</ul>';
    box.hidden = false;
    box.onmousedown = function (e) {
      var li = e.target.closest('li'); if (!li) return; e.preventDefault();
      state.dateFormat = li.dataset.fmt; box.hidden = true; layoutText(); updatePrice();
    };
  }

  // ---------------------------------------------------------------- init
  function init() {
    ui.app = $('#mpb-app');
    if (!ui.app) return;
    ui.pv = $('#mpb-pv'); ui.stageWrap = $('#mpb-stage-wrap'); ui.stage = $('#mpb-stage'); ui.canvasBox = $('#mpb-canvas-box'); ui.back = $('#mpb-back'); ui.front = $('#mpb-front'); ui.loader = $('#mpb-loader');
    ui.sizes = $('#mpb-sizes'); ui.shapes = $('#mpb-shapes'); ui.styles = $('#mpb-styles'); ui.markers = $('#mpb-markers'); ui.markerColors = $('#mpb-marker-colors');
    ui.stands = $('#mpb-stands'); ui.fonts = $('#mpb-fonts'); ui.search = $('#mpb-search'); ui.suggest = $('#mpb-suggest');
    ui.inTitle = $('#mpb-in-title'); ui.inLocation = $('#mpb-in-location'); ui.inDate = $('#mpb-in-date');
    ui.showDate = $('#mpb-show-date'); ui.showCoords = $('#mpb-show-coords'); ui.zoom = $('#mpb-zoom');
    ui.dims = $('#mpb-dims'); ui.attrib = $('#mpb-attrib'); ui.atc = $('#mpb-atc'); ui.standNote = $('#mpb-stand-note');
    ui.cartBtn = $('#mpb-cart-btn'); ui.cartCount = $('#mpb-cart-count'); ui.editable = $('#mpb-editable');
    ui.media = $('#mpb-media'); ui.pvSteps = $('.mpb-pv-steps', ui.media); ui.builder = $('#mpb-builder'); ui.pvToggle = $('#mpb-pv-toggle');
    ui.lines = $('#mpb-lines'); ui.reveal = $('#mpb-reveal'); ui.total = $('#mpb-total'); ui.save = $('#mpb-save'); ui.inst = $('#mpb-inst'); ui.del2 = $('#mpb-del2');
    ui.sticky = $('#mpb-sticky'); ui.stickyBtn = $('#mpb-sticky-btn'); ui.stickyLabel = $('#mpb-sticky-label'); ui.stickyTotal = $('#mpb-sticky-total'); ui.stickyK = $('#mpb-sticky-k');

    var mUrl = CFG.manifestUrl || '';
    assetBase = mUrl.split('?')[0].replace(/[^/]*$/, '');
    var params = new URLSearchParams(window.location.search);
    var designKey = params.get('design');
    if (designKey && restoreDesign(designKey)) maxStep = STEPS;
    var designParam = params.get('mpb_design');
    if (designParam && applyDesignJson(designParam)) maxStep = STEPS;

    fetch(mUrl).then(function (r) { return r.json(); }).catch(function (e) { console.error('[mpb] manifest', e); return { stands: {}, masks: {} }; }).then(function (m) {
      manifest = m;
      syncDisplay(); buildSizes(); fillFromPrice(); buildShapes(); buildStyles(); buildMarkers(); buildStands(); buildFonts();
      syncSelections(); syncInputs(); bind(); refreshCartCount(); updateDelivery();
      /* Transcy loads after us; once its rates are in (or the visitor switches currency), re-render every price. Watch ~20 s,
         then keep a light check on currency-switcher clicks. */
      var tcTries = 0, tcT = setInterval(function () { if (syncDisplay()) rerenderPrices(); if (++tcTries > 50) clearInterval(tcT); }, 400);
      document.addEventListener('click', function () { setTimeout(function () { if (syncDisplay()) rerenderPrices(); }, 1200); }, true);
      goStep(maxStep >= STEPS ? STEPS : 1);
      syncHeaderOffset(); onScroll();
      applyStandLayers();
      return waitFor(function () { return !!window.fabric; });
    }).then(function () {
      fabricCanvas = new fabric.StaticCanvas('mpb-canvas', { width: CANVAS_W, height: CANVAS_H, enableRetinaScaling: false, renderOnAddRemove: false });
      resizeCanvasCss();
      return ensureFont(state.font);
    }).then(function () {
      layoutText();
      schedulePreview(true);
    }).catch(function (e) { console.error('[mpb] init failed', e); });

    // debugging/test hooks (read-only use)
    window.MPB = { state: state, renderPrintImages: renderPrintImages, buildCartPayload: buildCartPayload, ready: function () { return readyPromise; },
      designJson: designJson,
      // full 300 dpi print (no iOS cap), same render path as add to cart; used by tools/render_from_order.py
      exportPrint: function () { return ensureFont(state.font).then(function () { layoutText(); return renderPrintImagesLegacy(); }); },
      idle: function () { return new Promise(function (res) { (function t() { if (!renderBusy && ui.loader.hidden) res(); else setTimeout(t, 50); })(); }); },
      uploadDesign: uploadDesign, select: selectSuggestion, go: function (n) { goStep(n); }, step: function () { return step; }, headerOffset: headerOffset, styles: MAP_STYLES, styleFor: function (id) { return PROVIDER.style(id); }, ensureLib: ensureLib,
      texts: function () { var o = {}; Object.keys(textObjs).forEach(function (k) { var t = textObjs[k]; o[k] = { text: t.text, fontSize: t.fontSize, width: Math.round(t.width), visible: t.visible, top: t.top, font: t.fontFamily }; }); return o; } };
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
